import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DualLedgerPanel from './DualLedgerPanel';
import {
  Box, Button, IconButton, MenuItem, Slider, TextField, Tooltip, Typography, LinearProgress,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ReplayIcon from '@mui/icons-material/Replay';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

/**
 * WHAT A DISABLED TRANSPORT ICON LOOKS LIKE.
 *
 * <h2>Why this has to be stated at all</h2>
 *
 * MUI's default disabled colour is {@code rgba(0,0,0,0.26)} — BLACK at 26%. On a light toolbar
 * that is a grey icon; on this dark glass panel it is invisible. Measured in a browser: the
 * jump-to-end and Step buttons both computed to that value and simply were not on screen, which
 * is how "the button isn't showing up" was reported for a control that was rendered, 30x30, in
 * the viewport and hit-testable.
 *
 * <p>Several controls in this row already carried an explicit override and the new one did not,
 * so it is declared ONCE here rather than retyped per button — the `#d4a017` arithmetic, in a
 * place where getting it wrong makes a control disappear rather than merely look wrong.
 */
const ICON_DISABLED = 'rgba(232,238,246,0.30)';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
  buildCumulativeStats, replayStateAt, tradesClosedBy,
  fmtMoney, fmtPercent, fmtPrice, fmtProfitFactor, sessionDateOf, sessionTimeOf,
} from '../utils/replayStats';
import useReplayClock from '../hooks/useReplayClock';
import DynaManDebugPanel from './DynaManDebugPanel';
import ReplayAnnualSummary from './ReplayAnnualSummary';
import ReplayWindowSummary from './ReplayWindowSummary';
import {
  breakpointSet, stopUpdatesAt, nextTradeAfter, previousTradeBefore, openTradeExitAt,
} from '../utils/replayDiagnostics';
import { annotationMarks, REPLAY_ANNOTATION_SERIES_ID } from '../utils/replayAnnotations';
import {
  buildPlayheadOverlay, playheadMarks, viewWindowFor, REPLAY_PLAYHEAD_SERIES_ID,
} from '../utils/replayPlayhead';
import { timeBandMarks, TIME_BAND_SERIES_ID } from '../utils/timeBandOverlay';
import { ledgerMarkerMarks, LEDGER_MARKER_SERIES_ID } from '../utils/ledgerMarkers';
import { ctsPillarMarks, CTS_PILLAR_SERIES_ID, CTS_PILLAR_PRICE_SERIES_ID } from '../utils/ctsPillars';
import { fanLineMarks, FAN_SERIES_ID } from '../utils/fanLines';
// The ACTIONS are not imported here on purpose: they are reached through
// `replayCopilot.applyHumanAction`, which is the one place an action name becomes a ledger call.
// Importing them alongside would invite a second switch in this component, and then the buttons
// and the hotkeys would have two implementations of one vocabulary.
import { initialDualState, compareLedgers, SIZING } from '../utils/dualLedger';
import useReplayHotkeys from '../hooks/useReplayHotkeys';
import { algoTradesUpTo, applyHumanAction, barAt, markAt } from '../utils/replayCopilot';
import { DEFAULT_CLICK_PEN, resolveChartClick } from '../utils/replayClickExecution';
import { focusWindow, pageWindow } from '../utils/replayViewport';
import { noteRefusal } from '../utils/dualLedger';
import {
  MANDATES, algoSharesAtFrame, algoTradesBetween, frameBounds, isAutoProgression,
  terminalAlgoTrade, visibleBands,
} from '../utils/replaySession';
import { createRevealBuffer, revealTo } from '../utils/replayReveal';
import {
  INTRO_PHASE, INTRO_ERASE_MS, emptyFor, erasePlan, erasedCursor, holdFor, isErased,
  primedViewWindow, sessionViewWindow,
} from '../utils/replayIntro';
import { formingRays } from '../utils/formingBands';
import { glassSurface, glassText } from '../utils/glassSurface';
import { useHighTransparency, toggleHighTransparency } from '../utils/glassTransparency';
import { pricePatchSeries } from '../utils/pricePatch';

/**
 * THE REPLAY HUDS: a half-tone layer over the live chart, not a dialog in front of it.
 *
 * <h2>Why it is an overlay and not another dialog</h2>
 *
 * The thing being validated is the CHART — where the entry sits against the bars around it,
 * whether the stop was where the rule says. A modal covers exactly that. So these panels float
 * over the chart, are `pointerEvents: none` except where they are controls, and the price action
 * reads through them.
 *
 * <h2>Two panels, because they answer two different questions</h2>
 *
 * The left is the BOOK: what the strategy has done up to this bar. The top is the POSITION: what
 * it is doing right now, and it is absent entirely when flat — a top bar showing an entry price
 * of 0.00 while the engine holds nothing is worse than no bar at all.
 *
 * <h2>Everything is "as of the playhead"</h2>
 *
 * Every figure comes from `replayStateAt`, which truncates the book to the current frame. A HUD
 * that showed final statistics during a replay would be showing the viewer the answer to the
 * question they are watching to work out.
 */

/**
 * THE FLOATING SURFACE, from the one place it is declared.
 *
 * <p>It was a local object here and a second, different one in `DynaManDebugPanel` — two panels
 * in the same docked column with different fills, borders and only one of them with a shadow.
 * See `utils/glassSurface.js` for why that is the shape that drifts.
 */
/*
 * NOT a module constant any more.
 *
 * <p>The fill follows the reader's transparency override, so it has to be read inside each
 * component that paints a surface. Only the background moves -- the blur, the border, the shadow
 * and the text colour never vary -- which is why this is one hook rather than four props.
 */
const useGlass = () => glassSurface({ transparent: useHighTransparency() });

/**
 * The window a `datazoom` announced, as the two values the threshold pan speaks in.
 *
 * <h2>FROM THE EVENT FIRST, because that is the path this codebase has already proven</h2>
 *
 * `EChartComponent`'s own handler reads a user's wheel or drag out of `params.batch[0]` and a
 * programmatic dispatch off the top level, and records exactly these two fields into its
 * `zoomRef`. So the event carries what is needed, and reading it costs nothing.
 *
 * <p>`getOption()` is the FALLBACK, not the first choice: it clones the whole option — thousands
 * of candles — which is affordable once per operator gesture and would not be once per bar. The
 * fallback exists because the batch shape is a detail of how the gesture reached ECharts, and a
 * window the overlay cannot read means no adoption at all.
 *
 * <p>The first x-axis dataZoom is the one: the others are the y-axis zooms, which would hand back
 * a price where a bar index belongs. Null when neither source answers, which leaves the previous
 * reference in place rather than clearing it — the honest degradation, and the one the Phase 4B
 * fake exercises.
 */
export function readWindow(instance, event) {
  const batch = event && Array.isArray(event.batch) && event.batch.length > 0
    ? event.batch[0] : event;
  if (batch && Number.isFinite(batch.startValue) && Number.isFinite(batch.endValue)) {
    return { startValue: Math.trunc(batch.startValue), endValue: Math.trunc(batch.endValue) };
  }
  if (!instance || typeof instance.getOption !== 'function') return null;
  let option;
  try {
    option = instance.getOption();
  } catch (e) {
    return null;
  }
  const zooms = (option && option.dataZoom) || [];
  const x = zooms.find((z) => z
    && Number.isFinite(z.startValue) && Number.isFinite(z.endValue));
  if (!x) return null;
  return { startValue: Math.trunc(x.startValue), endValue: Math.trunc(x.endValue) };
}



// LEGIBILITY PASS. These were 10/14 against a 55%-opacity label, which is readable on a
// designer's monitor and not while reading a number off a chart. Labels are now 12 at 80%
// opacity and values 17 — the panels are a data readout, not chrome.
const LABEL = { color: 'rgba(232,238,246,0.80)', fontSize: 12, letterSpacing: 0.5, lineHeight: 1.6 };
const VALUE = { fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.35 };

/**
 * One bar a second while auto-playing a trade, per the brief.
 *
 * <p>Slow on purpose and NOT taken from the speed slider: the slider is a viewing speed for
 * watching a run go by, and this is a reading speed for watching one trade. Auto-play sets it for
 * the duration and does not restore the old value afterwards — a control that silently moved the
 * slider back would make the slider lie about what the clock is doing.
 */
/**
 * How far the docked column sits from the chart's right edge, in pixels.
 *
 * <p>Was 20, which put the dock over the newest candles — the very bars a reader is checking the
 * engine's decision against. Exported so a test can assert it rather than a magic number drifting
 * back: the clearance is the whole point of the value, and a refactor that reset it to 20 would
 * look like a formatting change.
 */
export const DOCK_RIGHT_PX = 70;

/** What the transport says during each beat of the opening sequence. */
export const INTRO_NOTES = Object.freeze({
  ERASING: 'CLEARING THE CHART — removing historical bias',
  EMPTY: 'THE CANVAS IS EMPTY — no price, and no structure either',
  PRIMED: 'STRUCTURE PRIMED — the pillars are standing over empty space',
});

export const AUTO_PLAY_DELAY_MS = 1000;

const GAIN = '#4caf50';
const LOSS = '#ef5350';
const AMBER = '#ffb300';

/**
 * THE SPEED LADDER: seven detents from 0.2 ms/bar to 2 s/bar.
 *
 * <h2>The slider runs on the INDEX, not on the milliseconds</h2>
 *
 * MUI places a mark at its VALUE on a linear axis, so a ms-valued slider spanning 0.2 to 2000
 * puts six of these seven detents inside the leftmost 20% of the track and one at the far
 * right. It is usable and it feels nothing like smooth. Sliding over the INDEX gives seven
 * evenly spaced detents, and `scale` is what keeps the value label in milliseconds.
 *
 * <h2>Both ends are the brief's, and the fast one needed a mechanism</h2>
 *
 * The slowest is 2000 ms/bar as asked. **That retires the old 3 s detent**, which an earlier
 * note here defended as "how they watch a single trade resolve" — 2 s is the bound now, and
 * that is a deliberate narrowing rather than an oversight.
 *
 * <p>The fastest is 0.2 ms/bar: five times the old 1 ms ceiling. It is not a smaller timeout —
 * a nested `setTimeout` is clamped to about 4 ms whatever it is asked for, so the old "max" was
 * already running at roughly 250 bars a second rather than 1,000. `resolveSpeed` delivers it by
 * advancing FIVE BARS PER TICK, which is genuinely five times the throughput rather than a
 * smaller number under the same behaviour.
 */
export const SPEED_STEPS = [
  { ms: 0.2, label: '5×' },
  { ms: 1, label: '1' },
  { ms: 50, label: '50' },
  { ms: 120, label: '120' },
  { ms: 400, label: '400' },
  { ms: 1000, label: '1s' },
  { ms: 2000, label: '2s' },
];

/** The slider's own marks: evenly spaced indices, labelled with the speed they select. */
export const SPEED_MARKS = SPEED_STEPS.map((step, i) => ({ value: i, label: step.label }));

/**
 * The detent nearest a speed, so the slider shows where the clock actually is.
 *
 * <p>NEAREST ON A LOG SCALE, because the ladder is one: 120 sits between 50 and 400, and on a
 * linear measure it would snap to 50 — which is the wrong detent by a factor of more than two.
 */
export const speedIndexOf = (ms) => {
  const target = Math.log(Math.max(0.0001, ms));
  let best = 0;
  SPEED_STEPS.forEach((step, i) => {
    if (Math.abs(Math.log(step.ms) - target) < Math.abs(Math.log(SPEED_STEPS[best].ms) - target)) {
      best = i;
    }
  });
  return best;
};

/** How a speed reads on screen. Sub-millisecond settings are the stride, so say bars/tick. */
export const speedLabel = (ms, stride) => (stride > 1
  ? `${ms} ms / bar — ${stride} bars per tick`
  : `${ms} ms / bar`);

/*
 * EVERY COMPONENT AND EVERY STATIC `sx` IS DEFINED AT MODULE SCOPE.
 *
 * `Cell` was originally declared INSIDE ReplayPositionHud. A component defined during render is a
 * new type on every render, so React unmounts and remounts its whole subtree each time — six MUI
 * Boxes and twelve Typographies, with emotion re-serialising every style, on every bar of the
 * replay. Inline `sx` objects cost the same way in miniature: a fresh object identity defeats
 * emotion's cache.
 *
 * That is not a micro-optimisation here. It is the difference between a replay that runs at the
 * speed it is set to and one pinned near a bar a second regardless.
 */

/* Module scope, like every other static sx here -- a fresh object defeats emotion's cache. */
const SPEED_SLIDER = {
  color: AMBER,
  mt: 0.25,
  // The track is short inside the bar, so the labels have to stay out of each other's way.
  '& .MuiSlider-markLabel': { color: 'rgba(232,238,246,0.75)', fontSize: 10, top: 18 },
  '& .MuiSlider-mark': { background: 'rgba(255,255,255,0.45)', height: 5 },
  '& .MuiSlider-markActive': { background: AMBER },
  '& .MuiSlider-thumb': { width: 12, height: 12 },
};

/*
 * The ticker and the scope line, stacked. `minWidth: 0` is what lets the ellipsis below work:
 * without it a flex child refuses to shrink below its content and a long symbol would push the
 * COMPARE button out of the panel instead of truncating.
 */
const HEADER_STACK = { minWidth: 0, mr: 1 };

/*
 * LARGE, BOLD, AND FULL-CONTRAST -- GLASS_TEXT at alpha 1, where the labels beneath it sit at
 * 0.55-0.80. That gap is what makes it read as the panel's title rather than as another stat.
 *
 * Ellipsised rather than wrapped: `RELIANCE.XNSE` is 13 characters and the dock is 389px, so
 * a wrap is reachable on a narrow panel and would push every figure down a line.
 */
const SYMBOL_HEADING = {
  color: glassText(1),
  fontSize: 22,
  fontWeight: 800,
  lineHeight: 1.1,
  letterSpacing: 0.5,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const TRANSPORT_ROW = {
  display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', rowGap: 1,
};

/*
 * The speed block needs room for SEVEN labelled detents. At 150px they collided; 210 is the
 * narrowest that keeps `5x` and `2s` off each other at fontSize 10, and `pb` is the space the
 * labels sit in -- without it they overlap the row below once the bar wraps.
 */
const SPEED_BLOCK = { flex: '1 1 210px', px: 1.5, minWidth: 210, pb: 1.75 };

const STAT_BOX = { mb: 1.1 };
const CELL_BOX = { px: 1.75, borderRight: '1px solid rgba(255,255,255,0.08)' };
const CELL_BOX_LAST = { px: 1.75 };
const CELL_VALUE = { ...VALUE, fontSize: 13 };

function Stat({ label, value, colour }) {
  return (
    <Box sx={STAT_BOX}>
      <Typography sx={LABEL}>{label}</Typography>
      <Typography sx={VALUE} style={colour ? { color: colour } : undefined}>{value}</Typography>
    </Box>
  );
}
Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  colour: PropTypes.string,
};

/** One labelled figure in the top bar. `style`, not `sx`, for the one value that varies. */
function Cell({ label, value, colour, last }) {
  return (
    <Box sx={last ? CELL_BOX_LAST : CELL_BOX}>
      <Typography sx={LABEL}>{label}</Typography>
      <Typography sx={CELL_VALUE} style={colour ? { color: colour } : undefined}>
        {value}
      </Typography>
    </Box>
  );
}
Cell.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  colour: PropTypes.string,
  last: PropTypes.bool,
};

/* ── the left HUD: the running book ──────────────────────────────────────────────────────── */

/**
 * The three orders, with the sentence each one needs.
 *
 * <p>Module scope and ABOVE its consumer, like every other static object in this file: an array
 * rebuilt per render is a fresh identity for three Tooltips on every bar of the replay, and a
 * const declared below the component that reads it is the temporal dead zone this repository has
 * taken the whole app down with once.
 */
export const HUMAN_ACTIONS = [
  ['BUY', GAIN, 'Buy or cover at this bar\'s close. Already long does nothing; short reverses. (B)'],
  ['SELL', LOSS, 'Sell or short at this bar\'s close. Already short does nothing; long reverses. (S)'],
  ['FLAT', 'rgba(255,255,255,0.5)', 'Close the open discretionary position at this close. (X)'],
];

export function ReplayLedgerHud({
  state, sized, comparison, dual, onHumanAction, sizingModel, onSizingModelChange, currentBar,
  frozen, sessionScoped, autoProgression, closedTrades, clickPen, onClickPenChange,
  onFocusRange, barIndex, barTotal, symbol,
}) {
  const glass = useGlass();
  const transparent = useHighTransparency();
  const { stats, account } = state;
  const netColour = account && account.netPnl >= 0 ? GAIN : LOSS;

  // The custom window's two dates. Null is unbounded on that side, so the initial state is
  // "every trade" -- which is what the cumulative figures above already report, so an unopened
  // window agrees with the panel it sits in rather than contradicting it.
  // NOT named `window`: that shadows the global inside a component whose siblings bind key
  // listeners to it, and the shadow would be silent until one of them was moved in here.
  const [customWindow, setCustomWindow] = useState({ from: null, to: null });

  /**
   * Which book the panel is showing.
   *
   * <p>A TOGGLE rather than both at once: the single-book view is eleven rows and the
   * comparative view is ten more, and a column that has to be scrolled to is not being compared
   * with anything. Defaults to the algo view, because that is what this HUD has always shown
   * and a reader who has not opted into the copilot should see no change.
   */
  const [view, setView] = React.useState('ALGO');
  const comparing = view === 'DUAL' && !!comparison;

  /*
   * A TERMINATED SESSION OPENS ON THE COMPARISON, because that is the thing it produced.
   *
   * Switching the view for the reader is normally the wrong move — it overrides a choice they
   * made. Here the choice was "which book am I watching", and the session has just ended: the
   * answer to "how did I do" is the only remaining question, and leaving it one click away behind
   * a toggle labelled COMPARE would hide the result of the whole exercise.
   */
  React.useEffect(() => { if (frozen) setView('DUAL'); }, [frozen]);

  return (
    // NOT absolutely positioned any more. It is a block inside the docked column so the ledger
    // and the engine state read as one control centre; on a wide monitor the old split put the
    // two halves of one question a screen apart, and a reader's eyes did the joining.
    // pointerEvents INHERITED, not 'none'. It used to be none because the panel floated over the
    // chart and had nothing to click; inside a scrolling dock that makes it transparent to
    // hit-testing, so a wheel over the ledger would scroll the CHART behind it instead of the
    // column it belongs to.
    <Box data-testid="replay-ledger-hud" sx={{ ...glass, p: 1.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={HEADER_STACK}>
          {/*
            THE TICKER, AS A HEADING RATHER THAN A WATERMARK.
            ================================================

            <p>A WATERMARK CANNOT BE BOTH high-contrast and non-obscuring, and the brief asks
            for both: "bold, high-contrast, large-format" and "does not obscure or clash with
            the numerical performance metrics". Behind the numbers at a readable weight it
            competes with them; faint enough not to, it is not high-contrast. So it sits ABOVE
            the figures in its own line, which is prominent, legible and overlaps nothing.

            <p>It is the SCOPE of every number underneath, which is the same job the
            `through N / TOTAL` line does for the playhead -- so the two belong together, and
            the ticker goes first because it is the larger question.

            <p>It never disagrees with the run: the symbol is the TIMELINE's, and changing the
            chart's symbol exits the replay outright (a timeline is one symbol's bars indexed
            against the dates loaded when it started). So a scorecard showing GLD is always a
            GLD run, and a new symbol means a new session that renders its own.
          */}
          {symbol ? (
            <Typography data-testid="ledger-symbol" sx={SYMBOL_HEADING} title={symbol}>
              {symbol}
            </Typography>
          ) : null}
          <Typography sx={{ ...LABEL, color: frozen ? LOSS : AMBER, fontWeight: 700 }}>
          {/* THE HEADING SAYS WHICH QUESTION THE COLUMN ANSWERS, which matters now that the two
              views are scoped differently: the comparison counts the SESSION's trades and the
              single-book ledger counts the run's. Two numbers that disagree without a label
              between them read as a bug. */}
          {/*
              THE SCOPE IS IN THE HEADING, because without it two readings of this panel are
              indistinguishable from two different runs.
              ====================================================================

              <p>Every figure here is bounded at the PLAYHEAD -- that is the point of a replay,
              and `closedTrades` is `tradesClosedBy(timeline, clock.index)`. So launching the
              same deterministic timeline twice and reading the panel at two different bars gives
              two different numbers, and neither equals the Run Full Simulation total. That was
              reported as the replay being non-deterministic; the backend returns byte-identical
              trade lists, and what was missing was any statement of what the numbers cover.

              <p>`through N / TOTAL` is that statement. At the final bar it reads
              `through 8480 / 8480` and the figures equal the full run exactly, which is the
              parity a reader can now check rather than take on trust.
          */}
          {comparing
            ? (frozen ? 'FINAL — ALGO vs HUMAN' : `ALGO vs HUMAN${sessionScoped ? ' · session' : ''}`)
            : `RUNNING LEDGER${Number.isFinite(barIndex) && Number.isFinite(barTotal)
              ? ` · through ${barIndex + 1} / ${barTotal}` : ''}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/*
            THE TRANSPARENCY OVERRIDE, on the panel rather than in the setup dialog.
            ======================================================================

            <p>It is a READING adjustment made while looking at the thing being read: how much of
            the chart shows through depends on what is behind the panel right now, which is the
            one thing a modal closed before the session started cannot show. Putting it here also
            means it can be tried and undone in one click against the backdrop that prompted it.

            <p>It says the ALPHA rather than "high transparency", because that is the quantity
            and a reader comparing two states wants to know which one they are in. The tooltip
            carries the part a number cannot: that the lighter one is below the contrast standard
            and why it is still offered.
          */}
          <Tooltip
            arrow
            title={transparent
              ? 'Glass at 50% — lighter than the 4.5:1 contrast standard wants over a bright '
                + 'backdrop, which is why it is not the default. Click for 65%.'
              : 'Glass at 65% — the most transparent fill that keeps this text at 4.5:1 over the '
                + 'brightest thing the chart can put behind it. Click for 50%.'}
          >
            <Box
              component="button"
              type="button"
              data-testid="ledger-transparency-toggle"
              aria-pressed={transparent}
              aria-label="Panel transparency"
              onClick={toggleHighTransparency}
              sx={{
                ...LABEL,
                cursor: 'pointer',
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 1,
                color: transparent ? AMBER : 'rgba(255,255,255,0.75)',
                px: 0.75,
                py: 0.1,
                fontSize: 10,
              }}
            >
              {transparent ? '50%' : '65%'}
            </Box>
          </Tooltip>
          {comparison ? (
          <Box
            component="button"
            type="button"
            data-testid="ledger-view-toggle"
            onClick={() => setView(comparing ? 'ALGO' : 'DUAL')}
            sx={{
              ...LABEL,
              cursor: 'pointer',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 1,
              color: 'rgba(255,255,255,0.75)',
              px: 0.75,
              py: 0.1,
              fontSize: 10,
            }}
          >
            {comparing ? 'ALGO ONLY' : 'COMPARE'}
          </Box>
          ) : null}
        </Box>
      </Box>

      {/* THE FREEZE, STATED. A panel that has stopped tracking the playhead and does not say so
          is the worst outcome available: the figures look live and are not. It also names what was
          flattened, because "both books were closed at this bar" is the claim the final comparison
          rests on. */}
      {frozen ? (
        <Typography data-testid="replay-frozen-banner"
          sx={{ ...LABEL, color: LOSS, fontSize: 11, lineHeight: 1.45, mb: 1 }}>
          {`SESSION TERMINATED at bar ${frozen.atIndex + 1}`}
          {frozen.atDate ? ` · ${frozen.atDate}` : ''}
          {Number.isFinite(frozen.atPrice) ? ` · ${fmtPrice(frozen.atPrice)}` : ''}
          {'. Frozen — '}
          {frozen.algoFlattened || frozen.humanFlattened
            ? `${[frozen.algoFlattened ? 'the algo' : null,
              frozen.humanFlattened ? 'your' : null].filter(Boolean).join(' and ')} `
              + 'open position was marked out at this close, with the same costs.'
            : 'neither book held a position.'}
        </Typography>
      ) : null}

      {comparing ? (
        <DualLedgerPanel
          comparison={comparison}
          sizingModel={dual && dual.config ? dual.config.sizingModel : null}
          openHumanPosition={dual && dual.human ? dual.human.position : null}
          lastRefusal={dual && dual.human ? dual.human.lastRefusal : null}
          // THE BAR FED IN, every tick. The open position is re-priced at the playhead so it
          // moves with the chart; the LEDGER is not advanced by a bar, which is what keeps
          // stepping backwards correct. See replayCopilot.markAt.
          mark={markAt(dual && dual.human ? dual.human.position : null, currentBar)}
        />
      ) : (
        <>

      <Stat label="TRADES CLOSED" value={stats.trades} />
      <Stat
        label="WINS / LOSSES"
        value={(
          <>
            <span style={{ color: GAIN }}>{stats.wins}</span>
            <span style={{ color: 'rgba(232,238,246,0.4)' }}> / </span>
            <span style={{ color: LOSS }}>{stats.losses}</span>
          </>
        )}
      />
      <Stat
        label="WIN RATE"
        value={stats.trades > 0 ? `${stats.winRatePercent.toFixed(1)}%` : '—'}
      />
      {/* "n/a" until something has lost. Never a large number — an absent denominator is not a
          perfect score, and this is exactly when a viewer is forming an impression. */}
      <Stat label="PROFIT FACTOR" value={fmtProfitFactor(stats.profitFactor)} />

      {/* GROSS RETURN — the COMPOUNDED percentage, and the figure that must equal the
          Simulator's "Total Net PnL" tile for the same run.
          
          There was no gross return here at all, which is half of why the two surfaces appeared
          to disagree about Net PnL: the HUD only ever showed the SIZED dollar return from the
          account, and the Simulator only ever showed the UNSIZED compounded percentage. Those
          are different quantities and both are worth seeing, but with only one on each screen
          there was nothing a reader could hold against the other. */}
      <Stat
        label="GROSS RETURN"
        value={stats.trades > 0 ? fmtPercent(stats.totalNetPnlPercent) : '—'}
        colour={stats.totalNetPnlPercent > 0 ? GAIN : stats.totalNetPnlPercent < 0 ? LOSS : undefined}
      />

      {/* MAX DRAWDOWN sits with the percentage block because it IS a percentage, and because it
          is the one number here that cannot be reconstructed from the others: it is
          path-dependent, so a total cannot tell a steady climb from a round trip.

          The CURRENCY curve when the run is sized, the compounded percentage curve when it is
          not. It used to exist only on the dollar curve, so an unsized replay reported a flat
          0.00% — not a measurement, just the absence of a curve to measure, and it read as
          "riskless". See utils/portfolioMetrics. */}
      <Stat
        label="MAX DRAWDOWN"
        value={stats.trades > 0 ? `${stats.maxDrawdownPercent.toFixed(2)}%` : '—'}
        colour={stats.maxDrawdownPercent > 0 ? LOSS : undefined}
      />

      {sized && account ? (
        <>
          <Box sx={{ height: 1, background: 'rgba(255,255,255,0.10)', my: 1.2 }} />
          <Stat label="EQUITY" value={fmtMoney(account.equity)} />
          <Stat
            label="NET P&L"
            value={`${account.netPnl >= 0 ? '+' : ''}${fmtMoney(account.netPnl)}`}
            colour={netColour}
          />
          <Stat label="RETURN" value={fmtPercent(account.netPnlPercent)} colour={netColour} />

          {/* GROSS GAIN AND GROSS LOSS KEPT APART, not netted. "Up 40k" and "made 180k and gave
              back 140k" are the same net and completely different books, and the second is the
              one worth seeing while walking a trade sequence. */}
          <Box sx={{ height: 1, background: 'rgba(255,255,255,0.10)', my: 1.2 }} />
          <Stat
            label="TOTAL GAIN"
            value={`+${fmtMoney(stats.grossProfitMoney)}  (${
              fmtPercent(stats.grossProfitPercent)})`}
            colour={GAIN}
          />
          <Stat
            label="TOTAL LOSS"
            value={`-${fmtMoney(stats.grossLossMoney)}  (${
              fmtPercent(-stats.grossLossPercent)})`}
            colour={LOSS}
          />
        </>
      ) : (
        <>
          <Box sx={{ height: 1, background: 'rgba(255,255,255,0.10)', my: 1.2 }} />
          {/* Said plainly. A blank equity block reads as a loading state. Reachable only when a
              caller omits the account — the debugger always sends DEBUG_ACCOUNT. */}
          <Typography sx={{ ...LABEL, lineHeight: 1.5 }}>
            Unsized run — percentages only, no account.
          </Typography>
        </>
      )}
        </>
      )}

      {/* ANNUAL PERFORMANCE, bounded at the playhead like everything else in this panel.
          Collapsed by default: a 33-year run is thirty-three rows, and the dock is a column
          over a chart. It computes nothing of its own -- see ReplayAnnualSummary. */}
      <ReplayAnnualSummary trades={closedTrades} onFocusYear={onFocusRange} />

      {/*
        THE CUSTOM WINDOW, and its dates live HERE rather than in the component.
        A reader who sets a window and then flips the ALGO/HUMAN toggle, or whose panel
        re-renders on the next bar, must not have it cleared -- and a component holding its own
        state is a component that loses it the moment a parent decides to remount it. The HUD is
        rebuilt on every tick of the clock, so this is not hypothetical.
      */}
      <ReplayWindowSummary
        trades={closedTrades}
        from={customWindow.from}
        to={customWindow.to}
        onChange={setCustomWindow}
        onFocus={onFocusRange}
      />

      {/* THE COPILOT'S OWN CONTROLS, shown only when a caller wired an action handler. A
          discretionary book that cannot be traded is a column of zeros, and three buttons is
          the whole interface: the fill is the bar's close, so there is nothing to type. */}
      {onHumanAction ? (
        <>
          {/* WHAT THE NEXT CLICK WILL ACTUALLY DO. The fill is the playhead bar's close, and
              saying so is the difference between a button a reader trusts and one they press
              twice to find out. Absent when there is no usable bar, which is itself the answer. */}
          <Typography
            data-testid="human-fill-price"
            sx={{ ...LABEL, mt: 1.25, fontSize: 11, lineHeight: 1.4 }}
          >
            {currentBar
              ? `FILLS AT ${fmtPrice(currentBar.close)} — ${currentBar.date} close`
              : 'No usable bar at the playhead — orders will be refused'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
            {HUMAN_ACTIONS.map(([k, c, hint]) => (
              <Tooltip key={k} title={hint} arrow placement="top">
                <Box
                  component="button"
                  type="button"
                  data-testid={`human-${k.toLowerCase()}`}
                  onClick={() => onHumanAction(k)}
                  sx={{
                    ...LABEL,
                    flex: 1,
                    cursor: 'pointer',
                    background: 'transparent',
                    border: `1px solid ${c}`,
                    borderRadius: 1,
                    color: c,
                    py: 0.3,
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {k}
                </Box>
              </Tooltip>
            ))}
          </Box>

          {/*
            THE CLICK PEN. Which order Alt+Click places, and it is a PEN rather than a third
            modifier because the obvious third combination is Alt+Shift and Shift is already the
            spiral inspection gesture -- intercepted in the capture phase, so a handler bound for
            it would never even run.

            Shown only when a caller wired the click path, so a HUD without it does not advertise
            a gesture that does nothing.
          */}
          {onClickPenChange ? (
            <Box sx={{ mt: 0.75 }}>
              <Typography sx={{ ...LABEL, fontSize: 9, opacity: 0.8 }}>
                ALT+CLICK THE CHART TO
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }} data-testid="click-pen">
                {HUMAN_ACTIONS.map(([k, c]) => {
                  const active = clickPen === k;
                  return (
                    <Box
                      key={k}
                      component="button"
                      type="button"
                      data-testid={`click-pen-${k.toLowerCase()}`}
                      aria-pressed={active}
                      onClick={() => onClickPenChange(k)}
                      sx={{
                        ...LABEL,
                        flex: 1,
                        cursor: 'pointer',
                        // FILLED when selected, outlined when not: the pen has to be readable at
                        // a glance from across the panel, and a border alone is the difference a
                        // reader misses when they are looking at the chart rather than the HUD.
                        background: active ? c : 'transparent',
                        border: `1px solid ${c}`,
                        borderRadius: 1,
                        color: active ? '#0b0f14' : c,
                        py: 0.2,
                        fontSize: 9,
                        fontWeight: 700,
                      }}
                    >
                      {k}
                    </Box>
                  );
                })}
              </Box>
              <Typography sx={{ ...LABEL, fontSize: 9, opacity: 0.65, mt: 0.25, lineHeight: 1.4 }}>
                {/* THE DEVIATION, said on screen rather than only in the source. A reader who
                    clicks a price and gets the close has to be able to find out why without
                    reading a module. */}
                Fills at the clicked BAR&rsquo;s close, never the clicked price — a price fill
                would let every low be bought. Ahead of the playhead jumps there first; behind it
                is refused as hindsight.
              </Typography>
            </Box>
          ) : null}

          {/* THE SIZING MODEL, as a two-state toggle rather than a dropdown. There are two
              models and the choice restarts the book, so a control that opens a menu to offer
              two options is a click for nothing.

              It restarts BECAUSE two trades in one ledger sized by different rules are not
              comparable with each other, and nothing else on screen would say so. Said here
              rather than discovered. */}
          {onSizingModelChange ? (
            <Tooltip
              arrow
              placement="top"
              title={sizingModel === SIZING.FIXED_RISK
                ? 'Sized by the same risk budget and the same arithmetic the algo uses. '
                  + 'A click with no stop is sized on NOTIONAL and the trade records that. '
                  + 'Switch to match the algo share-for-share — this restarts the human book.'
                : 'Sized to the algo\'s own share count at this bar, inheriting its most recent '
                  + 'allocation while it is flat. Switch back to a fixed risk percentage — this '
                  + 'restarts the human book.'}
            >
              <Box
                component="button"
                type="button"
                data-testid="human-sizing-toggle"
                onClick={() => onSizingModelChange(
                  sizingModel === SIZING.FIXED_RISK ? SIZING.MATCH_ALGO : SIZING.FIXED_RISK)}
                sx={{
                  ...LABEL,
                  width: '100%',
                  mt: 0.5,
                  cursor: 'pointer',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: 1,
                  color: 'rgba(255,255,255,0.7)',
                  py: 0.2,
                  fontSize: 10,
                }}
              >
                {`SIZING: ${sizingModel === SIZING.FIXED_RISK ? 'FIXED RISK' : 'MATCH ALGO'}`}
              </Box>
            </Tooltip>
          ) : null}

          {/* THE KEYS, written down where the buttons are. A copilot is meant to decide while
              watching the bar, and a hotkey nobody can discover is a hotkey nobody uses. */}
          <Typography sx={{ ...LABEL, mt: 0.75, fontSize: 10, lineHeight: 1.5 }}>
            {/* THE LEGEND TELLS THE TRUTH ABOUT THE MODE. In MANUAL there is no timer, so the
                spacebar is unbound — and a legend still advertising "SPACE play" would read as a
                broken key rather than as a mode the operator chose. */}
            {autoProgression === false
              ? '← → step · B buy · S sell · X flat'
              : 'SPACE play · ← → step · B buy · S sell · X flat'}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}

ReplayLedgerHud.propTypes = {
  /** The run's ticker. Absent renders nothing rather than a placeholder. */
  symbol: PropTypes.string,
  /** Trades closed at or before the playhead — what the annual table is built from. */
  closedTrades: PropTypes.array,
  /** Which order Alt+Click places. Absent `onClickPenChange` hides the selector entirely. */
  clickPen: PropTypes.string,
  onClickPenChange: PropTypes.func,
  /** Moves the chart's viewport to a `{from, to}` date range. Absent makes the rows inert. */
  onFocusRange: PropTypes.func,
  state: PropTypes.object.isRequired,
  sized: PropTypes.bool,
  /** From `compareLedgers`. Absent means no copilot and no toggle — the HUD is unchanged. */
  comparison: PropTypes.object,
  /** The dual state, for the sizing label and the open-position line. */
  dual: PropTypes.object,
  /** Called with 'BUY' | 'SELL' | 'FLAT'. Absent hides the controls entirely. */
  onHumanAction: PropTypes.func,
  /** The active sizing model, for the toggle's own label. */
  sizingModel: PropTypes.string,
  /** Absent hides the sizing toggle rather than showing one that cannot change anything. */
  onSizingModelChange: PropTypes.func,
  /** `{date, close}` at the playhead, so the controls can state the price they will fill at. */
  currentBar: PropTypes.object,
  /** The frozen snapshot, or null. Present switches to the comparison and says it is frozen. */
  frozen: PropTypes.object,
  /** Whether the comparison counts the SESSION's trades rather than the whole run's. */
  sessionScoped: PropTypes.bool,
  /** The playhead, so the panel can state the scope of its own figures. */
  barIndex: PropTypes.number,
  barTotal: PropTypes.number,
  /** False is MANUAL CLICK, which changes what the key legend can honestly promise. */
  autoProgression: PropTypes.bool,
};

/**
 * Re-renders only when the BOOK changes, which on a real run is a handful of times in 2,000 bars.
 * Without this the left panel rebuilt itself on every tick to display identical numbers.
 */
export const MemoReplayLedgerHud = React.memo(ReplayLedgerHud,
  (a, b) => a.sized === b.sized && a.state.stats === b.state.stats
    && a.state.account === b.state.account
    // AND THE PLAYHEAD, because the heading now states the scope of its own figures. Without it
    // a memo caller would show "through 1 / 8480" over a panel at bar 4,000 -- a label that is
    // worse than none, since a reader would trust it.
    && a.barIndex === b.barIndex && a.barTotal === b.barTotal
    // AND THE SYMBOL. It is in the heading now, and a memo that ignored it would caption a GLD
    // scorecard with the previous run's ticker -- a label a reader would trust, which is worse
    // than none. The same reasoning the playhead pair carries above.
    && a.symbol === b.symbol);

/* ── the top HUD: the open position ──────────────────────────────────────────────────────── */

/**
 * Flush with the top edge, and ABSENT when flat.
 *
 * <p>When a trade closes on this very frame it stays up for that bar showing the exit reason,
 * because the moment of the exit is the thing being validated and it would otherwise vanish on
 * the same tick it happened.
 */
export function ReplayPositionHud({ state }) {
  const glass = useGlass();
  const trade = state.activeTrade;
  const closed = state.justClosed[0] || null;

  if (!trade && !closed) return null;

  const long = (trade || closed).direction === 'LONG';
  const pnl = trade ? trade.openPnlPercent : closed.pnlPercent;
  const colour = pnl >= 0 ? GAIN : LOSS;

  // The trailing floor when the rule has one, the structural stop otherwise. Both are shown when
  // they differ, because "the stop trailed" is only visible against where it started.
  const floor = trade && Number.isFinite(trade.trailingFloor) ? trade.trailingFloor : null;

  return (
    <Box
      data-testid="replay-position-hud"
      sx={{
        ...glass,
        position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
        zIndex: 12, display: 'flex', alignItems: 'center', py: 0.75,
        borderTopLeftRadius: 0, borderTopRightRadius: 0, borderTop: 'none',
        pointerEvents: 'none',
        borderBottom: `2px solid ${closed ? AMBER : colour}`,
      }}
    >
      <Cell label="POSITION" value={long ? 'LONG' : 'SHORT'} colour={long ? GAIN : LOSS} />

      {trade && (
        <>
          <Cell label="ENTRY" value={fmtPrice(trade.entryPrice)} />
          <Cell label="STOP" value={fmtPrice(trade.structuralStop)} colour={LOSS} />
          {floor !== null && (
            <Cell label="TRAILING FLOOR" value={fmtPrice(floor)} colour={AMBER} />
          )}
          <Cell label="HIGH WATER" value={fmtPrice(trade.highWaterMark)} />
          <Cell label="UNREALISED" value={fmtPercent(pnl)} colour={colour} />
          <Cell
            label="OPEN R"
            value={Number.isFinite(trade.openR) ? `${trade.openR.toFixed(2)}R` : '—'}
            colour={colour}
            last
          />
        </>
      )}

      {closed && (
        <Cell
          label={`CLOSED - ${closed.exitTimeframe}`}
          value={`${closed.exitReasonCode} · ${fmtPercent(closed.pnlPercent)}`}
          colour={closed.pnlPercent >= 0 ? GAIN : LOSS}
          last
        />
      )}
    </Box>
  );
}
ReplayPositionHud.propTypes = { state: PropTypes.object.isRequired };

/* ── the transport ───────────────────────────────────────────────────────────────────────── */

export function ReplayTransport({
  clock, state, symbol, onClose, nextTrade, previousTrade, onNextTrade, onPreviousTrade,
  tradeCount, tradeOrdinal, tradeExit, onAutoPlayTrade,
  session, mandate, onMandateChange, mandateLoading, frozen, onTerminate, onResetSession,
  autoProgression, intro, onPage,
}) {
  const glass = useGlass();
  const frame = state.frame;
  const date = frame ? sessionDateOf(frame.time) : null;
  const time = frame ? sessionTimeOf(frame.time) : null;
  const intraday = frame && frame.timeframe && frame.timeframe !== 'daily';

  const progress = clock.total > 1 ? ((clock.index + 1) / clock.total) * 100 : 0;

  return (
    <Box
      data-testid="replay-transport"
      sx={{
        ...glass,
        position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 12, px: 2, py: 1,
        /*
         * WIDE ENOUGH FOR ITS OWN CONTENTS, which 720px was not.
         *
         * The row's children carry minWidths -- the status note 190, the speed block 150, the
         * mandate 136 -- and with the eight transport buttons they total well past 720. A flex
         * row does not clip, it OVERFLOWS: the speed slider and the Terminate button were
         * rendered outside the grey panel, to its right, looking like stray controls that had
         * never been put inside it. They were inside it in the DOM the whole time.
         *
         * So the fix is the container and the wrap below, not a re-parenting.
         */
        width: 'min(1020px, calc(100% - 32px))',
        maxWidth: 'calc(100vw - 32px)',
        pointerEvents: 'auto',
      }}
    >
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 3, borderRadius: 2, mb: 1,
          background: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': { background: AMBER },
        }}
      />

      {/*
        WRAPS, so a narrow viewport folds the row instead of pushing controls out of the panel.
        `rowGap` is what keeps a folded second line from touching the first, and the whole
        point of the change is that nothing ends up outside the grey container at any width.
      */}
      <Box sx={TRANSPORT_ROW}>
        {/*
          THE PAGING PAIR, first in the row and SEPARATED from the transport by a divider.
          They are the only two controls here that do not move the playhead -- the clock is
          untouched and so is the ledger -- so grouping them with Restart and Back would put a
          control that scrolls the chart beside one that rewinds the run, which is the pair a
          reader is most likely to confuse under time pressure.

          Shown only when a caller wired them, like the pen selector: a button that scrolls
          nothing is worse than an absent one.
        */}
        {onPage ? (
          <>
            <Tooltip title="Page the chart one full screen LEFT. The clock does not move — this
              scrolls the view at its current zoom.">
              <span><IconButton size="small" data-testid="replay-page-left"
                onClick={() => onPage(-1)} sx={{ color: '#e8eef6' }}>
                <FirstPageIcon fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Tooltip title="Page the chart one full screen RIGHT. The clock does not move.">
              <span><IconButton size="small" data-testid="replay-page-right"
                onClick={() => onPage(1)} sx={{ color: '#e8eef6' }}>
                <LastPageIcon fontSize="small" />
              </IconButton></span>
            </Tooltip>
            <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.5,
              background: 'rgba(255,255,255,0.16)' }} />
          </>
        ) : null}
        <Tooltip title="Restart">
          <span><IconButton size="small" onClick={clock.restart} sx={{ color: '#e8eef6' }}>
            <ReplayIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        {/*
            TO THE LAST BAR, which is the control that made the parity UNREACHABLE.
            =====================================================================

            <p>Every figure in the Running Ledger is bounded at the playhead, so it equals the
            Run Full Simulation total only at the final bar. There was no way to GET there: the
            only seek control is the speed slider, playing 8,480 bars takes over an hour at a
            readable speed, and the Alt+Click jump needs a real pointer on the canvas.

            <p>So a reader who played until the candles stopped moving landed inside the last
            stretch of bars and read 147 trades against the full run's 148 -- reported as a
            calculation divergence, and it was an unreachable playhead. On the shipped SPY run
            the final trade closes on bar 8,459 of 8,480: twenty-one bars of chart in which the
            ledger is one trade short and nothing says so.

            <p>It uses `jump`, which PAUSES -- arriving at the end with the clock still running
            would immediately be indistinguishable from having pressed play.
        */}
        <Tooltip title="Jump to the last bar — where the ledger equals the full-run total">
          <span><IconButton size="small" data-testid="replay-to-end"
            onClick={() => clock.jump(clock.total - 1)}
            disabled={clock.atEnd || !!frozen || !!intro}
            sx={{ color: '#e8eef6', '&.Mui-disabled': { color: ICON_DISABLED } }}>
            {/* NOT LastPageIcon, which is two buttons away on the view pager. One glyph for a
                control that moves the CLOCK and another that moves only the VIEW would make the
                transport unreadable exactly where this fix is meant to make it legible. */}
            <KeyboardDoubleArrowRightIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        <Tooltip title="Back one bar">
          <span><IconButton size="small" onClick={clock.back} disabled={clock.index === 0}
            sx={{ color: '#e8eef6', '&.Mui-disabled': { color: ICON_DISABLED } }}>
            <SkipPreviousIcon fontSize="small" />
          </IconButton></span>
        </Tooltip>
        {/* PLAY IS ABSENT IN MANUAL, not disabled.
            There is no timer to start, so a greyed-out play button would be a control that cannot
            ever become available — and a reader would spend time working out why. The mode is
            named in the readout instead, beside the bar counter. */}
        {autoProgression !== false ? (
          <Tooltip title={frozen ? 'The session is terminated — reset it to play again' : clock.playing ? 'Pause' : 'Play'}>
            <span><IconButton onClick={clock.toggle} disabled={!!frozen || !!intro}
              data-testid="replay-play"
              sx={{ color: '#0b1016', background: AMBER, '&:hover': { background: '#ffc94d' },
                '&.Mui-disabled': { background: 'rgba(255,255,255,0.12)' } }}>
              {clock.playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton></span>
          </Tooltip>
        ) : null}
        {/* LABELLED, not just an icon. This is the debugger's primary control now - stepping one
            bar at a time is the whole workflow - and an unlabelled chevron next to a play button
            reads as "skip", which is the opposite of what it does. */}
        {/* THE STEP, which in MANUAL mode is THE control and is styled as one.
            Named "Next Bar" there, because that is what the progression mode calls it and a
            reader who chose Manual Click is looking for the thing they were promised. */}
        <Tooltip title={autoProgression === false
          ? 'Unveil the next bar (right arrow)'
          : 'Advance the FSM exactly one bar (pauses playback)'}>
          <span>
            <Button
              size="small"
              onClick={clock.step}
              disabled={clock.atEnd || !!frozen || !!intro}
              data-testid="replay-step-button"
              startIcon={<SkipNextIcon fontSize="small" />}
              sx={autoProgression === false
                ? {
                  color: '#0b1016', background: AMBER, textTransform: 'none', fontSize: 13,
                  fontWeight: 700, ml: 0.5, px: 1.25,
                  '&:hover': { background: '#ffc94d' },
                  '&.Mui-disabled': { color: 'rgba(232,238,246,0.30)', background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.12)' },
                }
                : {
                  color: '#e8eef6', textTransform: 'none', fontSize: 13, fontWeight: 600, ml: 0.5,
                  border: '1px solid rgba(255,255,255,0.18)', px: 1,
                  '&:hover': { background: 'rgba(255,255,255,0.08)' },
                  // The manual branch above has this and the auto branch did not, so Step
                  // vanished at the end of an auto run rather than greying out.
                  '&.Mui-disabled': { color: ICON_DISABLED },
                }}
            >
              {autoProgression === false ? 'Next Bar' : 'Step'}
            </Button>
          </span>
        </Tooltip>

        {/* AUTO-PLAY TRADE. One bar a second from here to the exit bar, and not one bar past it.
            Offered only while a premise is open, because "play this trade" has no meaning when
            there is no trade — and a control that is enabled everywhere but only sometimes does
            something is worse than one that visibly does not apply. */}
        {autoProgression !== false ? (
        <Tooltip
          title={tradeExit === null
            ? 'No trade is open on this bar — use Next Trade to land on one'
            : clock.playing
              ? 'Pause'
              : `Advance one bar a second and stop on the exit bar (${tradeExit + 1})`}
        >
          <span>
            <Button
              size="small"
              onClick={onAutoPlayTrade}
              disabled={tradeExit === null || clock.index >= tradeExit}
              data-testid="replay-autoplay-trade"
              startIcon={clock.playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              sx={{
                textTransform: 'none', fontSize: 13, fontWeight: 600, ml: 0.5, px: 1.25,
                color: clock.stopAt !== null ? '#0b1016' : '#e8eef6',
                background: clock.stopAt !== null ? GAIN : 'transparent',
                border: '1px solid rgba(255,255,255,0.18)',
                '&:hover': {
                  background: clock.stopAt !== null ? '#5cbf60' : 'rgba(255,255,255,0.08)',
                },
                '&.Mui-disabled': { color: 'rgba(232,238,246,0.30)' },
              }}
            >
              {clock.playing && clock.stopAt !== null ? 'Playing…' : 'Auto-Play Trade'}
            </Button>
          </span>
        </Tooltip>
        ) : null}

        {/* NEXT TRADE. The primary control: it fast-forwards through the empty bars and HARD
            PAUSES on the bar that commits a premise. It replaced a "pause on trades" toggle,
            which was the wrong primitive - a switch that changes what a LATER play press does is
            a mode, and a mode is something to remember. A button that goes where you asked is
            not. */}
        <Tooltip
          title={nextTrade === null
            ? 'No further trade entries ahead in this window'
            : `Fast-forward to the next trade entry (bar ${nextTrade + 1}) and pause there`}
        >
          <span>
            <Button
              size="small"
              onClick={onNextTrade}
              disabled={nextTrade === null}
              data-testid="replay-next-trade"
              endIcon={<FastForwardIcon fontSize="small" />}
              sx={{
                textTransform: 'none', fontSize: 13, fontWeight: 600, ml: 0.75, px: 1.25,
                color: '#0b1016', background: AMBER,
                '&:hover': { background: '#ffc94d' },
                '&.Mui-disabled': { color: 'rgba(232,238,246,0.35)', background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)' },
              }}
            >
              Next Trade
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={previousTrade === null
            ? 'No earlier trade entry in this window'
            : `Back to the previous trade entry (bar ${previousTrade + 1})`}
        >
          <span>
            <IconButton
              size="small"
              onClick={onPreviousTrade}
              disabled={previousTrade === null}
              data-testid="replay-previous-trade"
              sx={{ color: '#e8eef6', ml: 0.25 }}
            >
              <FastRewindIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ minWidth: 190, ml: 1 }}>
          <Typography sx={{ ...LABEL, lineHeight: 1.3 }}>
            {/* THE OPENING SEQUENCE NAMES ITSELF. A transport whose controls are all dead and
                whose chart is emptying itself looks broken for the second and a half it takes,
                and "it is doing something deliberate" is one line of text. */}
            {intro ? (
              <span data-testid="replay-intro-note" style={{ color: AMBER, fontWeight: 700 }}>
                {/* THREE PHASES, THREE SENTENCES. The empty beat used to borrow the primed one,
                    which told a reader the pillars were standing while the canvas held nothing —
                    the one moment in the sequence where the note contradicted the chart. */}
                {INTRO_NOTES[intro] || INTRO_NOTES.ERASING}
              </span>
            ) : null}
            {intro ? null : `${symbol} · bar ${clock.index + 1} / ${clock.total}`}
            {/* WHERE YOU ARE IN THE TRADE SEQUENCE, which "bar 1,284 of 2,000" cannot say. It is
                the number a reader is actually tracking while walking a run. */}
            {!intro && tradeCount > 0 && (
              <span style={{ color: AMBER }}>
                {` · trade ${Math.min(tradeOrdinal, tradeCount)} / ${tradeCount}`}
              </span>
            )}
          </Typography>
          <Typography sx={{ fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
            {date || '—'}
            {time && intraday && <span style={{ color: AMBER }}> {time}</span>}
            {frame && (
              <span style={{ color: 'rgba(232,238,246,0.5)' }}> · {frame.timeframe}</span>
            )}
            {frame && <span style={{ color: AMBER }}> · {frame.state}</span>}
          </Typography>
        </Box>

        <Box sx={SPEED_BLOCK}>
          {autoProgression === false ? (
            // THE SLIDER IS GONE, not disabled: it sets a speed, and there is no speed. Saying
            // which mode is in force is the useful thing to put in its place.
            <Typography data-testid="replay-manual-note"
              sx={{ ...LABEL, lineHeight: 1.4, fontSize: 11 }}>
              MANUAL CLICK — one bar per Next Bar or right-arrow. No timer.
            </Typography>
          ) : (
          <>
          <Typography sx={{ ...LABEL, lineHeight: 1 }} data-testid="replay-speed-label">
            SPEED — {speedLabel(clock.delayMs, clock.stride)}
          </Typography>
          {/* Adjustable WHILE PLAYING. The clock reads the delay from a ref inside a
              self-rescheduling timeout, so dragging this does not restart the timer — which
              would stall the replay for as long as the drag lasted. */}
          <Slider
            size="small"
            data-testid="replay-speed"
            value={speedIndexOf(clock.delayMs)}
            onChange={(_, v) => {
              const i = Array.isArray(v) ? v[0] : v;
              clock.setDelayMs(SPEED_STEPS[Math.max(0, Math.min(SPEED_STEPS.length - 1, i))].ms);
            }}
            min={0}
            max={SPEED_STEPS.length - 1}
            marks={SPEED_MARKS}
            step={null}
            scale={(i) => SPEED_STEPS[i].ms}
            valueLabelDisplay="auto"
            valueLabelFormat={(ms) => `${ms} ms`}
            sx={SPEED_SLIDER}
          />
          </>
          )}
        </Box>

        {/* ── THE ALGO MANDATE ────────────────────────────────────────────────────────────
            Which engine the algo book trades. Shown only for a SESSION, because a run started
            from the research dialog carries that dialog's own provider and its own levers — and
            offering to swap the provider there would discard a configuration the operator built
            a control at a time.

            CHANGING IT RE-RUNS THE ENGINE. The algo book comes from the backend, so a different
            mandate is a different fetch; the dropdown says so rather than letting a reader
            discover it from a loading spinner. The canvas is unaffected — it keeps drawing every
            overlay the session ticked, which is what makes execution and visualisation
            independent. */}
        {session && onMandateChange ? (
          <Tooltip
            arrow
            placement="top"
            title={'The engine the ALGO book trades. Changing it re-runs the engine over the same '
              + 'bars and restarts the human book, because the book it is compared against has '
              + 'been replaced. The canvas keeps every overlay this session drew, and your own '
              + 'trades stay free to act on any of them.'}
          >
            <TextField
              select
              size="small"
              value={mandate || ''}
              onChange={(e) => onMandateChange(e.target.value)}
              disabled={!!mandateLoading || !!frozen}
              label="Algo Mandate"
              inputProps={{ 'data-testid': 'algo-mandate' }}
              sx={{
                minWidth: 136, ml: 0.75,
                '& .MuiInputBase-input': { color: '#e8eef6', fontSize: 13, py: 0.75 },
                '& .MuiInputLabel-root': { color: 'rgba(232,238,246,0.7)', fontSize: 12 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
                '& .MuiSvgIcon-root': { color: '#e8eef6' },
              }}
            >
              {MANDATES.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </TextField>
          </Tooltip>
        ) : null}

        {/* ── TERMINATE ───────────────────────────────────────────────────────────────────
            Stops the clock, flattens BOTH books at this bar's close with the same costs, and
            freezes the comparison at this exact bar. Distinct from Exit replay, which leaves the
            feature entirely: this one ENDS THE SESSION and keeps the result on screen, which is
            the thing an operator wants after making their call. */}
        {onTerminate ? (
          <Tooltip
            arrow
            placement="top"
            title={frozen
              ? `Terminated at bar ${frozen.atIndex + 1}${frozen.atDate ? ` (${frozen.atDate})` : ''}`
              : 'End the session here: stop the clock, flatten BOTH books at this bar\'s close '
                + 'with the same commission and slippage, and freeze the comparison at this bar.'}
          >
            <span>
              <Button
                size="small"
                onClick={onTerminate}
                disabled={!!frozen || !!intro}
                data-testid="replay-terminate"
                sx={{
                  textTransform: 'none', fontSize: 13, fontWeight: 700, ml: 0.75, px: 1.25,
                  color: LOSS, border: `1px solid ${LOSS}`,
                  '&:hover': { background: 'rgba(239,68,68,0.12)' },
                  '&.Mui-disabled': { color: 'rgba(232,238,246,0.30)',
                    borderColor: 'rgba(255,255,255,0.12)' },
                }}
              >
                {frozen ? 'Terminated' : 'Terminate'}
              </Button>
            </span>
          </Tooltip>
        ) : null}

        {/* AFTER TERMINATION: back to the start, or stay where it froze.
            Both offered explicitly. "Stay" is not a no-op control — without it the only visible
            next step is Reset, and a reader who wanted to keep reading the frozen bar would
            reasonably think they had to leave. */}
        {frozen && onResetSession ? (
          <Tooltip arrow placement="top"
            title={`Back to bar ${(session ? 0 : 0) + 1} of the session with a fresh human book. `
              + 'Leave it alone to keep reading the chart at the freeze bar.'}>
            <Button
              size="small"
              onClick={onResetSession}
              data-testid="replay-reset-session"
              sx={{
                textTransform: 'none', fontSize: 13, fontWeight: 600, ml: 0.75, px: 1.25,
                color: '#0b1016', background: AMBER, '&:hover': { background: '#ffc94d' },
              }}
            >
              Reset to start
            </Button>
          </Tooltip>
        ) : null}

        {/* EXIT, LABELLED. There was only the × below, at the far right of a crowded transport
            bar, and it read as "dismiss this panel" rather than "leave replay mode" -- which is
            how a reader came to believe replay could not be exited at all. A word costs nothing
            and is the difference between a control that is present and one that is findable. */}
        <Tooltip title="Leave replay mode and return the chart to normal">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloseIcon fontSize="small" />}
            onClick={onClose}
            data-testid="exit-replay"
            sx={{
              color: '#e8eef6', borderColor: 'rgba(232,238,246,0.45)', ml: 0.5,
              '&:hover': { borderColor: '#e8eef6', background: 'rgba(232,238,246,0.08)' },
            }}
          >
            Exit replay
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
}
ReplayTransport.propTypes = {
  /** Pages the chart's viewport by one screenful: -1 left, +1 right. Absent hides the pair. */
  onPage: PropTypes.func,
  /** Frame index of the next trade entry ahead, or null when none remains. */
  nextTrade: PropTypes.number,
  /** Frame index of the previous trade entry, or null. */
  previousTrade: PropTypes.number,
  onNextTrade: PropTypes.func,
  onPreviousTrade: PropTypes.func,
  /** How many trade entries this window contains, for the position readout. */
  tradeCount: PropTypes.number,
  /** Which one the playhead is on or past, 1-based, for the position readout. */
  tradeOrdinal: PropTypes.number,
  /** Frame the OPEN trade closes on, or null when flat. Auto-play's destination. */
  tradeExit: PropTypes.number,
  onAutoPlayTrade: PropTypes.func,
  clock: PropTypes.object.isRequired,
  state: PropTypes.object.isRequired,
  symbol: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  /** The session, or null for a run started from the research dialog. */
  session: PropTypes.object,
  /** Which engine the algo book trades. */
  mandate: PropTypes.string,
  /** Absent hides the selector rather than offering one that cannot change anything. */
  onMandateChange: PropTypes.func,
  mandateLoading: PropTypes.bool,
  /** The frozen snapshot, or null. Disables play, step and the mandate. */
  frozen: PropTypes.object,
  onTerminate: PropTypes.func,
  onResetSession: PropTypes.func,
  /** False is MANUAL CLICK: no play button, no slider, no auto-play, one bar per press. */
  autoProgression: PropTypes.bool,
  /** 'ERASING' | 'PRIMED' while the opening sequence runs, null once the session is live. */
  intro: PropTypes.string,
};

/* ── the whole layer ─────────────────────────────────────────────────────────────────────── */

/**
 * THE CLOCK LIVES HERE, NOT IN App.
 *
 * <h2>A correction about the measurements that led here</h2>
 *
 * The throughput figures originally recorded in this file (0.6, then 0.9 bars a second against a
 * configured 8.3) were an ARTEFACT. They were taken in an automated browser tab whose
 * `visibilityState` was `hidden`, and Chrome clamps `setTimeout` to roughly once a second in a
 * backgrounded tab — 0.9 bars a second IS that clamp, not the code. Four consecutive changes
 * failed to move the number, which was the clue.
 *
 * <p>Real-world throughput has therefore NOT been measured. What has been measured, with
 * synchronous timing that the clamp does not affect, is that one playhead update costs ~74ms and
 * a pan another ~91ms on a chart carrying 8,716 candles. That is a real cost and it is why the
 * structure below is still worth having.
 *
 * <h2>Why the clock is here anyway</h2>
 *
 * Holding the playhead index in `App` means every bar re-renders a 4,500-line component and its
 * whole subtree, to move a number in three small panels. Holding it here confines a tick to those
 * panels; `App` keeps only the timeline, which changes once per run. That is the right shape on
 * its own terms, independently of any number.
 *
 * <p>The playhead effect lives here for the same reason, and reaches the chart through
 * `getChartInstance` rather than through React state — moving the view is not a render.
 */
export default function DynaManReplayOverlay({
  timeline, dates, axisDates, getChartInstance, onClose, initialDelayMs, account, ctsPoints,
  session, onMandateChange, mandateLoading, barData, chartType, introMs,
}) {
  /**
   * THE SESSION'S FRAME BOUNDS, and why the clock's `total` is the session's END.
   *
   * <h2>The ceiling is expressed as the clock's total, not watched for afterwards</h2>
   *
   * A watcher — "pause once the index reaches the end" — reads the index one render LATE, so it
   * overshoots by a bar and lands the panel on a bar outside the session. Handing the clock a
   * total of `to + 1` makes the ceiling structural instead: play stops there, step clamps there,
   * and `bar X / N` counts the session rather than the run. The index stays the FRAME index
   * throughout, which every annotation and every state lookup depends on.
   *
   * <p>The floor is a jump rather than an offset, for the same reason: an offset would make
   * `clock.index` mean something different from a frame index, and five modules read it as one.
   * Stepping back below the floor is therefore possible and deliberately harmless — those bars are
   * drawn anyway, and the comparison counts only trades from the floor onward.
   */
  const bounds = useMemo(
    () => frameBounds(timeline, session?.startDate, session?.endDate),
    [timeline, session]);

  const total = session
    ? Math.min(bounds.to + 1, timeline?.frames?.length || 0)
    : (timeline?.frames?.length || 0);

  /**
   * THE AXIS THE CHART ACTUALLY DRAWS, which is NOT `dates`.
   *
   * <h2>The defect this closes</h2>
   *
   * `App` renders `extendedBarData` — the real series plus 250 synthetic future weekdays whose
   * OHLC is all the last real close — and hands this component `barData.dates`, the unpadded one.
   * The initial render of the band overlay goes through `chartDataProcessor`, which uses the
   * chart's own axis and therefore DID reach into the future; the per-tick merge below used the
   * short list and therefore did not. So the forward pillars were drawn once and then erased by
   * the first tick of the clock, which reads as them never having existed.
   *
   * <h2>Which decorations get which list, and why that split is not arbitrary</h2>
   *
   * <pre>
   *   axisDates   bands, Top/Bottom rays, CTS pillars     PURE CALENDAR — known at bar one
   *   dates       playhead, entries, exits, vectors        PRICE — must stop at the playhead
   * </pre>
   *
   * A generation band and a CTS timing point are astronomical: drawing them ahead of the playhead
   * reveals nothing the engine did not already know, and seeing the bars animate INTO them is the
   * entire point of the forward canvas. A trade marker is the opposite — it is the answer.
   */
  const axis = useMemo(
    () => (Array.isArray(axisDates) && axisDates.length ? axisDates : (dates || [])),
    [axisDates, dates]);

  /**
   * THE TRADE FRAMES — a map of where the entries are, and NO LONGER a brake on the clock.
   *
   * <h2>Play now runs to the end of the session</h2>
   *
   * These used to be handed to the clock as standing breakpoints, so pressing PLAY stopped on
   * every entry. The reasoning was that the two controls should agree about where a run stops.
   * On a real run they disagree with the operator instead: SPY's session carries 142 trades, so
   * play advanced a few bars, halted, and had to be pressed again — a hundred and forty-two
   * times to watch one session through. An animation that has to be restarted that often is not
   * an animation.
   *
   * <p>So the clock is given NO standing breakpoints. Play stops at the session's last bar or
   * when the operator stops it — pause, step, a hotkey, terminate — and nothing else.
   *
   * <p>The set itself stays, because two things still want it and neither is a brake:
   * `atBreakpoint` draws the BREAKPOINT chip when the playhead is sitting on an entry, and
   * NEXT TRADE still uses `playTo`, which is a one-shot destination the operator asked for
   * rather than a standing rule they have to remember is armed.
   *
   * <p>Memoised on the timeline: a fresh Set every render would make the clock's effect see a new
   * dependency each tick and rebuild its timer.
   */
  const breakpoints = useMemo(() => breakpointSet(timeline), [timeline]);
  const clock = useReplayClock(total, initialDelayMs, null, breakpoints);

  // Recomputed per bar, which is cheap: the list is one entry per trade, not one per frame.
  const nextTrade = nextTradeAfter(timeline, clock.index);
  const previousTrade = previousTradeBefore(timeline, clock.index);
  const tradeOrdinal = useMemo(
    () => (timeline?.breakpoints || []).filter((i) => i <= clock.index).length,
    [timeline, clock.index]);

  // The exit frame of whatever premise is open here, or null between trades. Auto-play's
  // destination, and the reason it cannot run on into the following trade.
  const tradeExit = openTradeExitAt(timeline, clock.index);

  // Prefix-summed ONCE per timeline. The clock can run at 1ms, and recomputing the book per tick
  // is O(frames x trades) of main-thread work between paints.
  const cumulative = useMemo(() => buildCumulativeStats(timeline), [timeline]);
  const state = useMemo(
    () => replayStateAt(timeline, clock.index, cumulative),
    [timeline, clock.index, cumulative]);

  const sized = useMemo(() => !!(timeline && timeline.netStatistics), [timeline]);

  /*
   * THE LATEST TIMELINE AND INDEX, read through refs.
   *
   * <p>Declared HERE, above the two consumers below — the copilot's action handler and the chart
   * effect's rAF callback. Both need the values at the moment they FIRE rather than the ones their
   * closure was built with, which is exactly what a ref gives them; naming `clock.index` as a
   * dependency instead would be correct and would also rebuild the effect on every tick.
   *
   * <p>Order matters beyond style: a hook body is evaluated top to bottom, so a `useCallback`
   * written above these consts reads them in the temporal dead zone the first time it is created
   * on some React paths. `App.tdz.test.js` exists because this repository has taken the whole app
   * down that way once already.
   */
  const timelineRef = useRef(timeline);
  const indexRef = useRef(clock.index);
  useEffect(() => { timelineRef.current = timeline; }, [timeline]);
  useEffect(() => { indexRef.current = clock.index; }, [clock.index]);

  // The clock itself, for the same reason: `useReplayClock` returns a fresh object every render,
  // so a callback naming `clock.jump` as a dependency would be rebuilt every tick -- and the
  // click handler is bound to the zrender layer, so rebuilding it means unbinding and rebinding
  // a listener sixty times a second during playback.
  const clockRef = useRef(clock);
  useEffect(() => { clockRef.current = clock; }, [clock]);

  /*
   * THE REVEAL BUFFER AND THE ERASE ARE DECLARED HERE, above everything that reads them.
   *
   * `live` and `drawable` are read by the hotkey binding and by the chart effect, both of which
   * sit below.
   * Leaving this block in its natural reading position — next to the chart effect it feeds —
   * put `erasing` in the temporal dead zone and took the whole overlay down on mount with
   * `Cannot access 'erasing' before initialization`. Lint passed; the tests caught it.
   */
  /**
   * THE REVEAL BUFFER: the price series, truncated at the playhead.
   *
   * <h2>Why a buffer and not a slice per tick</h2>
   *
   * A step forward reveals ONE bar. `revealTo` writes only the slots that changed, so the work is
   * proportional to the move rather than to a 9,000-column axis — and stepping BACK hides again,
   * which is what stops a one-way reveal showing the operator a future they had just stepped away
   * from.
   *
   * <p>Rebuilt when the axis or the series changes, which is a new symbol or a new run. A buffer
   * carried across one of those would hold the previous instrument's prices at the previous
   * instrument's indices — the exact shape of the defect that made a replay draw QQQ's playhead
   * over AAPL's chart.
   */
  const revealRef = useRef(null);
  useEffect(() => {
    // FLOORED AT THE SESSION'S FIRST BAR. The erase clears the chart to nothing to remove
    // historical bias; a buffer floored at 0 would put every bar of history back on the very next
    // frame, and the theatre would have said something the chart immediately contradicted.
    revealRef.current = createRevealBuffer((axis || []).length, session ? bounds.from : 0);
  }, [axis, barData, session, bounds.from]);

  // The chart type, read through a ref for the same reason everything else here is: the patch has
  // to restate the series TYPE, and restating the wrong one silently converts a line chart into
  // candles mid-run. That is a recorded defect, not a hypothetical.
  const chartTypeRef = useRef(chartType);
  useEffect(() => { chartTypeRef.current = chartType; }, [chartType]);
  const barDataRef = useRef(barData);
  useEffect(() => { barDataRef.current = barData; }, [barData]);

  /* ── the erase ─────────────────────────────────────────────────────────────────────────── */

  /**
   * THE THEATRE: wipe the chart right to left, prime the empty canvas, then start.
   *
   * <h2>Only for a SESSION, and only when it is asked for</h2>
   *
   * The research dialog's replay has run without any of this since it shipped, and a transition
   * that appeared there would be a change to a different feature. `introMs <= 0` starts LIVE
   * outright — which is also what every test that is not about the intro uses, because an
   * animation in front of an assertion is a wait, not a subject.
   */
  const introBudget = Number.isFinite(introMs) ? introMs : INTRO_ERASE_MS;
  const wantsIntro = !!session && introBudget > 0;
  const [introPhase, setIntroPhase] = useState(
    wantsIntro ? INTRO_PHASE.ERASING : INTRO_PHASE.LIVE);

  /** A new run re-runs the sequence; the same run must not. */
  useEffect(() => {
    setIntroPhase(wantsIntro ? INTRO_PHASE.ERASING : INTRO_PHASE.LIVE);
  }, [timeline, session, wantsIntro]);

  /**
   * The wipe itself.
   *
   * <h2>a) THE INSTANT FUTURE CLEAR falls out of the buffer's own semantics</h2>
   *
   * The first frame reveals every REAL bar and nothing else, so the 250 synthetic weekdays
   * `extendBarData` pads with — drawn until now as a flat tail of doji at the last close — are
   * gone in that one frame, before a single bar has been popped. Nothing special is done for it
   * because nothing needs to be: "ahead of the view" and "not a real bar" are the same set here.
   *
   * <h2>b) THEN THE REVERSE DELETION, time-boxed rather than one bar a frame</h2>
   *
   * One bar a frame over 8,466 bars is two minutes and twenty seconds. `erasePlan` turns the
   * budget into a step, so the wipe takes the same ~1.2s on a 250-bar upload and a 33-year series
   * — which it must, because it is a transition and not a progress bar.
   *
   * <p>`requestAnimationFrame` rather than a timer: this is a visual effect and nothing else
   * depends on its cadence, so it should run at the display's rate and stop dead when the tab is
   * hidden. (The replay CLOCK is a `setTimeout` for the opposite reason — a backgrounded replay
   * that silently crawled would leave a viewer at a position that means nothing.)
   *
   * <p>ONE `setOption` per frame, merged by id, carrying only the price. No `notMerge`, which is
   * what a full tear-down and its flash would be.
   */
  const eraseRef = useRef(null);
  const eraseWatchdogRef = useRef(null);
  useEffect(() => {
    if (introPhase !== INTRO_PHASE.ERASING) return undefined;

    const instance = getChartInstance?.();
    const bars = (barDataRef.current?.ohlc || []).length;
    if (!instance || bars === 0) {
      setIntroPhase(INTRO_PHASE.EMPTY);
      return undefined;
    }

    /*
     * THE PILLARS GO FIRST, in a call of their own.
     *
     * The chart's own option carries the bands from the moment `handleStartReplay` builds that
     * overlay, so without this the wipe runs against a backdrop of structure — the price being
     * removed from a chart that already knows the answer. They come back in one frame at PRIMED.
     *
     * Its own `setOption` rather than riding the first wipe frame: every frame of the loop
     * carries the two price series and nothing else, which is what keeps ECharts from
     * re-resolving decorations seventy times for one transition. One call before the loop is not
     * part of the loop.
     */
    instance.setOption({
      series: [
        { id: TIME_BAND_SERIES_ID, ...timeBandMarks([], axis, null) },
        // BOTH halves of the pillars -- the rail one carries the verticals and the masked
        // marks, the price one the revealed marks. Clearing one and not the other would leave
        // the previous chart's marks on whichever axis was missed.
        { id: CTS_PILLAR_SERIES_ID, ...ctsPillarMarks([], axis, null).rail },
        { id: CTS_PILLAR_PRICE_SERIES_ID, ...ctsPillarMarks([], axis, null).price },
        { id: FAN_SERIES_ID, ...fanLineMarks([], null) },
      ],
    });

    // A buffer of its own, UNFLOORED: the wipe runs over the chart as it was, which is every bar,
    // and it has to be able to reach nothing. The session's floored buffer is built separately
    // and is already empty when the wipe hands over.
    const wipe = createRevealBuffer((axis || []).length, 0);
    revealTo(wipe, barDataRef.current, bars - 1);
    const { step } = erasePlan(bars, introBudget);

    let cursor = bars - 1;
    let done = false;
    const draw = () => {
      instance.setOption({
        series: pricePatchSeries(chartTypeRef.current, [...wipe.data], [...wipe.volumes]),
      });
    };
    draw();                                   // frame 1: every real bar, no synthetic tail

    /** However the wipe ends — frame by frame, or cut short — it ends EMPTY and it ends PRIMED. */
    const finish = () => {
      if (done) return;
      done = true;
      if (eraseRef.current !== null) cancelAnimationFrame(eraseRef.current);
      eraseRef.current = null;
      revealTo(wipe, barDataRef.current, -1);
      draw();
      setIntroPhase(INTRO_PHASE.EMPTY);
    };

    const tick = () => {
      cursor = erasedCursor(cursor, step);
      revealTo(wipe, barDataRef.current, cursor);
      draw();
      if (isErased(cursor)) { finish(); return; }
      eraseRef.current = requestAnimationFrame(tick);
    };
    eraseRef.current = requestAnimationFrame(tick);

    /*
     * THE WATCHDOG, because `requestAnimationFrame` DOES NOT FIRE IN A HIDDEN TAB.
     *
     * Found in a browser, not in a test: driven through CDP the page reports
     * `document.visibilityState === 'hidden'`, rAF never runs, and the sequence stops wherever it
     * had got to — transport controls dead, note reading "CLEARING THE CHART", indefinitely. The
     * same thing happens to anyone who launches a session and switches tab before it finishes.
     *
     * Coming back un-pauses rAF and it would complete, so this is not the only thing standing
     * between a real user and a working session. It is the thing standing between them and a
     * window that is stuck for as long as they are not looking at it — and the theatre is the
     * optional half of this feature, so when it cannot run, it is dropped rather than waited for.
     *
     * Generous (three budgets, at least a second) so it never pre-empts a wipe that is merely
     * running on a slow display. A timer is the right instrument precisely because it is the one
     * thing here that a hidden tab still delivers.
     */
    eraseWatchdogRef.current = setTimeout(finish, Math.max(1000, introBudget * 3));

    return () => {
      done = true;
      if (eraseRef.current !== null) cancelAnimationFrame(eraseRef.current);
      eraseRef.current = null;
      if (eraseWatchdogRef.current !== null) clearTimeout(eraseWatchdogRef.current);
      eraseWatchdogRef.current = null;
    };
    // `axis` and the budget are the only inputs that can change what the wipe does; the bars and
    // the chart type are read through refs so a redraw underneath it does not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introPhase, axis, introBudget]);

  /**
   * b) THE EMPTY CANVAS, held. Nothing is drawn: no price, and no structure either.
   *
   * <p>Two seconds at the shipped budget. The wipe is motion and this is the stillness after it,
   * and the stillness is what makes the removal read as deliberate rather than as a repaint —
   * which is the whole reason the structure is not put up until this expires.
   *
   * <p>A `setTimeout` and a phase, so React owns the transition: nothing blocks, the transport
   * keeps rendering, and the clock — which is paused through the whole sequence — is untouched.
   * A busy-wait here would freeze the component for two seconds and take the transport with it.
   */
  useEffect(() => {
    if (introPhase !== INTRO_PHASE.EMPTY) return undefined;
    const id = setTimeout(() => setIntroPhase(INTRO_PHASE.PRIMED), emptyFor(introBudget));
    return () => clearTimeout(id);
  }, [introPhase, introBudget]);

  /**
   * c) THE PRIMED CANVAS: nothing but the pillars, over the session's own date range.
   *
   * <p>The structure is drawn by the same merge the live replay uses — `timeBandMarks` and
   * `ctsPillarMarks` are pure calendar and have never needed a bar — so priming is a matter of
   * drawing them with the price empty and the view moved, rather than of a second renderer.
   *
   * <p>Held, because without a hold the empty canvas exists for one frame and the thing it is
   * there to show — the structure known before any price — is never seen. The duration scales
   * with the configured budget: a sequence set to flick through should not pause for seven
   * hundred milliseconds in the middle of itself.
   */
  useEffect(() => {
    if (introPhase !== INTRO_PHASE.PRIMED) return undefined;
    const id = setTimeout(() => setIntroPhase(INTRO_PHASE.LIVE), holdFor(introBudget));
    return () => clearTimeout(id);
  }, [introPhase, introBudget]);

  const live = introPhase === INTRO_PHASE.LIVE;
  /**
   * Whether the ordinary draw may run at all.
   *
   * <p>ERASING and EMPTY are both "the chart is not the replay's yet": the first is owned by the
   * wipe loop, and the second is owned by nothing, which is the point of it. Reading this as
   * `!erasing` — the shape before EMPTY existed — would put the pillars back up the instant the
   * wipe finished and delete the pause entirely.
   */
  const drawable = introPhase === INTRO_PHASE.PRIMED || introPhase === INTRO_PHASE.LIVE;

  /* ── the copilot ───────────────────────────────────────────────────────────────────────── */

  /**
   * THE HUMAN BOOK, beside the algo one, over the same bars.
   *
   * <h2>Why FIXED_RISK is the default here, where Phase 1's default is MATCH_ALGO</h2>
   *
   * `MATCH_ALGO` is unanswerable until the engine has held something: early in a run there is no
   * allocation to match, and the ledger correctly refuses. On a 33-year replay that is the first
   * several hundred bars — the control would be dead exactly where an operator first reaches for
   * it, which reads as a broken button rather than as a sizing model with a precondition.
   *
   * <p>`FIXED_RISK` is always answerable AND is the model the algo itself is sized by: the same
   * risk percentage, against the same rolling-equity rule, through the same arithmetic in
   * `accountCosts`. So the default is the one that makes the two columns comparable, and
   * MATCH_ALGO is one click away for the bars where matching is what the question is.
   */
  const startingEquity = useMemo(() => {
    if (timeline?.netStatistics && Number.isFinite(timeline.netStatistics.startingEquity)) {
      return timeline.netStatistics.startingEquity;
    }
    return Number.isFinite(account?.startingEquity) ? account.startingEquity : null;
  }, [timeline, account]);

  const [sizingModel, setSizingModel] = useState(SIZING.FIXED_RISK);

  const [dual, setDual] = useState(() => initialDualState({
    sizingModel: SIZING.FIXED_RISK,
    startingEquity,
    riskPercent: timeline?.netStatistics?.riskBudgetPercent,
    account,
  }));

  /*
   * A NEW RUN RESETS THE BOOK, for the same reason the clock resets its index: a human ledger
   * carried across timelines would price trades taken on one symbol's bars into another's
   * equity curve, and the comparison would be against a book that never existed.
   *
   * Keyed on the timeline object and on the sizing model, because changing the model mid-book
   * would leave two trades in one ledger sized by different rules — which the ledger records per
   * trade, but which makes the aggregate a blend of two experiments. Starting again is the honest
   * answer, and it is cheap: nothing has been committed anywhere.
   */
  useEffect(() => {
    setDual(initialDualState({
      sizingModel,
      startingEquity,
      riskPercent: timeline?.netStatistics?.riskBudgetPercent,
      account,
    }));
  }, [timeline, sizingModel, startingEquity, account]);

  /**
   * One action, one bar, one place.
   *
   * <p>The HUD buttons and the hotkeys both land here with the same three-word vocabulary, so
   * there is no second switch to add a fourth action to. The functional `setDual` is what makes it
   * safe to call from a key handler while the clock is running: the state it applies to is the one
   * at the moment of the press, not the one the last render happened to see.
   */
  const handleHumanAction = useCallback((kind) => {
    setDual((current) => applyHumanAction(current, kind, timelineRef.current, indexRef.current));
  }, []);


  /**
   * Both books scored by the ONE shared reducer, as of the playhead.
   *
   * <p>The algo side is truncated to the trades it has closed by now. Showing its finished book
   * against a human book four bars old would not be a comparison of anything — and it would
   * flatter whichever side happened to be ahead at the end.
   *
   * <p>With a SESSION it is bounded at both ends: the operator can only trade inside the window,
   * so counting the engine's thirty prior years against their four trades compares nothing. The
   * single-book Running Ledger keeps its cumulative meaning, which is a different question and is
   * labelled as one.
   */
  const comparison = useMemo(
    () => compareLedgers(dual, session
      ? algoTradesBetween(timeline, bounds.from, clock.index)
      : algoTradesUpTo(timeline, clock.index)),
    [dual, timeline, clock.index, session, bounds.from]);

  /* ── termination ───────────────────────────────────────────────────────────────────────── */

  /**
   * THE FROZEN SNAPSHOT, or null while the session is live.
   *
   * <p>Holding the snapshot rather than a `terminated` boolean is what makes the freeze mean
   * something: the panel renders THIS comparison, computed once at the termination bar, so no
   * later render can move it. A boolean plus the live comparison would still track the playhead if
   * anything nudged the index — and the thing being promised is a figure that cannot change.
   */
  const [frozen, setFrozen] = useState(null);

  /** Cleared when the run changes, like every other piece of per-run state here. */
  useEffect(() => { setFrozen(null); }, [timeline, session]);

  /**
   * TERMINATE: stop the clock, flatten both books at this bar's close, and freeze the snapshot.
   *
   * <h2>Both books, or the comparison is decided by what was left out</h2>
   *
   * The human side goes through the ordinary reducer, so it is priced exactly as every other
   * discretionary trade is. The engine's open premise is MARKED OUT at the same close through the
   * same `accountCosts` module — see `terminalAlgoTrade`, which stamps it so it cannot be mistaken
   * for an exit the FSM decided. Flattening only the human would compare a closed book against one
   * still holding a position, and on this rule set one open premise can outweigh every closed
   * trade together.
   *
   * <h2>Zero subsequent ticks</h2>
   *
   * `clock.pause()` clears the timer and abandons any auto-play destination. The freeze then
   * disables play, step and the hotkeys, so nothing can restart it — a terminated session that
   * could be resumed by pressing space would be a snapshot of nothing.
   */
  const handleTerminate = useCallback(() => {
    clock.pause();
    const index = clock.index;
    const bar = barAt(timeline, index);

    const flat = applyHumanAction(dual, 'FLAT', timeline, index);
    setDual(flat);

    const closed = session
      ? algoTradesBetween(timeline, bounds.from, index)
      : algoTradesUpTo(timeline, index);
    const terminal = terminalAlgoTrade(
      state.activeTrade, bar, account, algoSharesAtFrame(timeline, index));
    const algoBook = terminal ? [...closed, terminal] : closed;

    setFrozen({
      atIndex: index,
      atDate: bar ? bar.date : null,
      atPrice: bar ? bar.close : null,
      comparison: compareLedgers(flat, algoBook),
      algoFlattened: !!terminal,
      humanFlattened: !!(dual.human && dual.human.position),
    });
  }, [clock, timeline, dual, session, bounds.from, state.activeTrade, account]);

  /** Back to the session's first bar with a fresh human book — a new attempt at the same window. */
  const handleResetSession = useCallback(() => {
    setFrozen(null);
    setDual(initialDualState({
      sizingModel, startingEquity, riskPercent: timeline?.netStatistics?.riskBudgetPercent, account,
    }));
    clock.jump(bounds.from);
  }, [clock, bounds.from, sizingModel, startingEquity, timeline, account]);

  const displayComparison = frozen ? frozen.comparison : comparison;

  /*
   * DECLARED HERE, below `frozen` and `live`, and that is load-bearing.
   *
   * The click handler reads both, and a `useCallback` dependency array is evaluated during
   * RENDER -- so with this block in its natural reading position beside `handleHumanAction` the
   * whole overlay threw `Cannot access 'frozen' before initialization` on mount. Lint passed and
   * the build compiled; eight suites went red. The same temporal-dead-zone failure
   * `App.tdz.test.js` exists for, which this file has now paid for twice.
   */
  /** Which order Alt+Click places. A pen, not a modifier — see `replayClickExecution`. */
  const [clickPen, setClickPen] = useState(DEFAULT_CLICK_PEN);

  /**
   * POINT-AND-CLICK EXECUTION: Alt+Click the chart to place the selected order.
   *
   * <h2>One bar, one fill, and the SAME reducer the keys use</h2>
   *
   * It ends in `applyHumanAction`, so a clicked order and a pressed one are the same operation
   * priced the same way — which is the brief's own requirement, and the reason there is no second
   * fill model here to disagree with the first.
   *
   * <h2>The jump comes FIRST, and the index is passed explicitly</h2>
   *
   * `clock.jump` is a state update, so `indexRef` has not moved by the time the next line runs.
   * The resolved index is therefore handed to the reducer directly rather than read back — the
   * same reason `handleHumanAction` reads a ref instead of the rendered value.
   *
   * <p>Jumping reveals every bar in between (`revealTo` writes the whole span), so a reader who
   * clicks forty bars ahead has those forty bars drawn and then trades the one they aimed at.
   */
  const handleChartClick = useCallback((params) => {
    const instance = getChartInstance?.();
    if (!instance || typeof instance.convertFromPixel !== 'function') return;
    const event = params && (params.event || params);
    // `params.event` for a series-item click, the zrender event itself for a blank-area one. Both
    // carry the modifiers and the offsets, which is all the resolver needs.
    const x = event && (event.offsetX ?? event.zrX);
    const y = event && (event.offsetY ?? event.zrY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;

    /*
     * SERIES 0 FIRST, the grid as the fallback. Series 0 is the candle series, which is what the
     * brief names; `gridIndex: 0` is what `EChartComponent` already uses and answers even when a
     * click lands where no series has data — which, with price revealed only to the playhead, is
     * every click ahead of it. Without the fallback the forward half of this gesture would
     * silently do nothing.
     */
    const point = instance.convertFromPixel({ seriesIndex: 0 }, [x, y])
      || instance.convertFromPixel({ gridIndex: 0 }, [x, y]);
    if (!point) return;

    const verdict = resolveChartClick({
      event,
      pen: clickPen,
      barIndex: Math.round(point[0]),
      playheadIndex: indexRef.current,
      barCount: (timelineRef.current?.frames || []).length,
      enabled: !frozen && live,
    });
    // NULL IS NOT OURS. A plain click belongs to the drawing tools and the bar selection, and
    // recording a refusal for one would fill the HUD with complaints about other gestures.
    if (!verdict) return;
    if (!verdict.ok) {
      setDual((current) => noteRefusal(current, verdict.reason));
      return;
    }
    if (verdict.jump) clockRef.current.jump(verdict.index);
    setDual((current) =>
      applyHumanAction(current, verdict.kind, timelineRef.current, verdict.index));
  }, [getChartInstance, clickPen, frozen, live]);

  /**
   * Bound on the ZRENDER layer, not through `instance.on('click')`.
   *
   * <p>`instance.on('click')` only fires when the click lands on a series ITEM, and the forward
   * half of this gesture is a click on EMPTY CANVAS — price is revealed only to the playhead, so
   * there is no item ahead of it to hit. `getZr().on('click')` sees every click on the canvas,
   * which is the whole area the gesture is defined over.
   *
   * <p>Falls back to `instance.on` when there is no zrender, which is what a fake chart in a test
   * has; both paths reach the same handler, so neither is a second implementation.
   */
  useEffect(() => {
    const instance = getChartInstance?.();
    if (!instance) return undefined;
    const zr = typeof instance.getZr === 'function' ? instance.getZr() : null;
    const target = zr && typeof zr.on === 'function' ? zr : instance;
    if (typeof target.on !== 'function') return undefined;
    target.on('click', handleChartClick);
    return () => { if (typeof target.off === 'function') target.off('click', handleChartClick); };
  }, [getChartInstance, handleChartClick, timeline]);

  /**
   * THE KEYS. Space plays, the arrows step, B/S/X trade.
   *
   * <p>Bound only while a replay is open AND NOT FROZEN. Declared here, below `frozen`, because
   * that is what the enable flag reads — and because a terminated session that could be resumed
   * by pressing space would make the snapshot a figure nobody could rely on.
   *
   * <p>Stepping through the clock rather than through a local index, so a key press and the
   * corresponding button are the same operation: `clock.step` pauses, which is what makes a bar
   * you asked to look at stay on screen.
   */
  /**
   * MANUAL progression has no timer, so the spacebar has nothing to toggle.
   *
   * <p>`onToggle: undefined` rather than a no-op function: the hook's optional call then does
   * nothing at all, and the key legend says so. A spacebar that silently did nothing while the
   * legend still advertised "SPACE play" would read as a broken key rather than as a mode.
   */
  const autoProgression = isAutoProgression(session);

  // NOT UNTIL THE SEQUENCE IS LIVE. A key press during the erase would move a playhead whose bars
  // are being deleted, and the first live frame would then land somewhere nobody chose; a press
  // during the empty beat or the primed one would act on a transport whose own buttons are
  // disabled, which is the kind of disagreement nobody finds by looking at the screen.
  useReplayHotkeys(!!timeline && !frozen && live, {
    onToggle: autoProgression ? clock.toggle : undefined,
    onStep: clock.step,
    onBack: clock.back,
    onHumanAction: handleHumanAction,
  });

  /*
   * AND THE TIMER IS NEVER LEFT RUNNING IN MANUAL.
   *
   * Belt and braces, deliberately: the transport hides play and the hotkey is unbound, so nothing
   * SHOULD start it. But a mode whose guarantee is "no timer" must not depend on every control
   * having remembered — a single surviving path to `clock.play` would make the mode a suggestion.
   * `clock.step` and `clock.back` both pause, so this only ever fires on a path that got through.
   */
  useEffect(() => {
    if (!autoProgression && clock.playing) clock.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `clock` is fresh every render
  }, [autoProgression, clock.playing]);

  /**
   * THE SESSION OPENS AT ITS OWN FIRST BAR.
   *
   * <p>`useReplayClock` resets to 0 when its total changes, which is frame 0 of the RUN — 1993 on
   * SPY. A session that started there would make the chosen window decorative. One jump, on the
   * session rather than on every render, so a reader who has stepped somewhere is not dragged back.
   */
  useEffect(() => {
    if (!session || !timeline) return;
    clock.jump(bounds.from);
    // `clock` is a fresh object each render; naming it would re-run this on every tick and pin the
    // playhead to the session start for ever. The jump belongs to the session, which is what the
    // dependency list says.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, timeline, bounds.from]);

  /**
   * Move the playhead — COALESCED to at most one chart update per animation frame.
   *
   * <h2>The chart costs ~165ms a bar, and at speed it does not need every one</h2>
   *
   * Updating the playhead costs ~74ms sustained and panning another ~91ms on a chart carrying
   * 8,716 candles — measured synchronously, so unlike the throughput figures this one is real.
   * At the 60ms and "max" settings that is more redraws than the clock asks for and more than an
   * eye can resolve.
   *
   * <p>So: fewer draws rather than a faster chart. The HUD ticks at the clock's rate
   * because those numbers are the point; the chart redraws at most once per frame and always
   * at the LATEST position, skipping the intermediate bars. At 400ms nothing is skipped; at max
   * speed the playhead sweeps smoothly across bars the eye could never have resolved anyway.
   *
   * <p>A ref holds the pending target so a burst of ticks collapses into one draw rather than
   * queueing dozens that each arrive too late to matter.
   */
  const frame = state.frame;
  const pendingRef = useRef(null);
  const rafRef = useRef(null);

  /**
   * THE AUTO-PAN, and the operator's share of it.
   *
   * <p>`claiming` is raised across the overlay's own `dispatchAction` so the events it provokes
   * are not mistaken for a gesture; `last` is the window it last asked for, so an identical one
   * is never re-sent; `live` is the window the threshold pan measures against — null until the
   * animation starts, which is what makes the first live frame zoom in from the primed range,
   * and REPLACED by the operator's own window whenever they pan or zoom.
   *
   * <p>A ref rather than state, deliberately: adopting a window must not re-render anything —
   * the view is the chart's, not React's — and a render in the middle of a pan is the stutter
   * this whole draw path is written to avoid.
   */
  const autoViewRef = useRef({ claiming: false, last: null, live: null });

  /** A new run starts from the session's own window rather than the previous run's. */
  useEffect(() => {
    autoViewRef.current = { claiming: false, last: null, live: null };
  }, [timeline, session]);

  /**
   * THE ONE PLACE A VIEWPORT CHANGE IS DISPATCHED.
   *
   * <h2>It raises the same `claiming` flag the threshold pan does, and that is the point</h2>
   *
   * ECharts announces every dataZoom, ours included, and the overlay's listener tells its own
   * from an operator's by that flag alone. A ledger-driven focus that did NOT claim would be
   * adopted as a gesture — harmless by luck, because the next line adopts it deliberately, but it
   * would mean the two paths disagreed about who moved the view. One claim, one adoption, stated
   * here rather than inferred from which function happened to call it.
   *
   * <h2>And it ADOPTS the result, so the threshold pan measures against it</h2>
   *
   * A reader who focuses 2008 and then plays will have the view follow the playhead again — at
   * THEIR zoom, because `sessionViewWindow` takes its span from the current window. That is the
   * Phase 7B contract and it is deliberate: the alternative is a view that stops tracking the
   * playhead because somebody clicked a year once, which is the surrender that phase reversed.
   * While the clock is PAUSED, which is when a reader scrubs, nothing moves it back.
   */
  const applyView = useCallback((view) => {
    const instance = getChartInstance?.();
    if (!instance || typeof instance.dispatchAction !== 'function' || !view) return false;
    const auto = autoViewRef.current;
    auto.last = view;
    auto.live = view;
    auto.claiming = true;
    try {
      instance.dispatchAction({
        type: 'dataZoom', startValue: view.startValue, endValue: view.endValue,
      });
    } finally {
      // `finally`, for the reason the tick's own dispatch has one: a throw inside ECharts that
      // left the flag raised would make every later gesture read as the overlay's.
      auto.claiming = false;
    }
    return true;
  }, [getChartInstance]);

  /**
   * A DATE RANGE, from the annual table or the custom window.
   *
   * <p>Resolved against `axis` — the PADDED date list the chart is actually drawing — and not
   * against `dates`. The two differ by 250 synthetic weekdays at the right-hand end, and a focus
   * resolved against the shorter one would be correct for every year but the last.
   */
  const handleFocusRange = useCallback((range) => {
    applyView(focusWindow(axis, range || {}));
  }, [applyView, axis]);

  /**
   * ONE SCREENFUL, left or right, at the current scale.
   *
   * <p>The window comes from `autoViewRef.live` when the overlay knows it, and from the chart
   * otherwise — which is the case before anything has panned, i.e. the first time a reader
   * presses the button. `readWindow(instance, null)` is the documented `getOption()` fallback;
   * it clones the option, which is why the tick path refuses to use it and why a button press
   * can afford to.
   */
  const handlePage = useCallback((direction) => {
    const instance = getChartInstance?.();
    if (!instance) return;
    const current = autoViewRef.current.live || readWindow(instance, null);
    if (!current) return;
    applyView(pageWindow(current, direction, (axis || []).length));
  }, [applyView, axis, getChartInstance]);

  /*
   * LISTENING FOR THE GESTURE, not polling for it.
   *
   * ECharts announces every dataZoom, ours included, so the handler has to tell the two apart.
   *
   * <h2>THE GESTURE IS ADOPTED, not surrendered to</h2>
   *
   * It used to be a surrender: the first datazoom this overlay did not dispatch handed the view
   * to the operator for the rest of the run, on the reasoning that a reader who zooms out to
   * look at the structure must not have the chart snatched back on the next bar.
   *
   * <p>That is half right, and the half it gets wrong is the one that matters: it also stopped
   * the playhead being tracked AT ALL, so a reader who adjusted the view once then watched the
   * animation walk off the right-hand edge and keep going into bars they could not see.
   *
   * <p>So the operator's window is now ADOPTED as the reference the threshold pan measures
   * against. Inside it, nothing moves -- the view is theirs, at their zoom, for as long as the
   * playhead stays in it. When the playhead reaches its right edge the ordinary threshold pan
   * fires and re-centres at 25%, AT THE SPAN THEY CHOSE. The chart is never snatched back and
   * never abandons the playhead.
   *
   * <p>Read from the EVENT the gesture arrived on, which is the path `EChartComponent`'s own
   * handler already uses, with `getOption()` as a fallback. A gesture is a human action -- a
   * handful per run at most -- so even the fallback's clone is affordable there, where it would
   * not be on the per-bar dispatch path.
   *
   * <h2>A FLAG HELD ACROSS THE CALL, not a counter -- and the counter was a real bug</h2>
   *
   * It used to be one: `pending += 1` per dispatch, `pending -= 1` per announced event, anything
   * left over read as the operator. That assumes EXACTLY ONE event per dispatch, which is an
   * assumption about ECharts rather than a property of it -- this chart carries four dataZoom
   * components, and a dispatch answered by two events spent the credit on the first and
   * surrendered on the second. Permanently, on the very first pan: after that the window never
   * moved again however far the playhead travelled, which is precisely the reported failure.
   *
   * <p>Counting better cannot fix it, because the opposite miscount is just as wrong: a dispatch
   * that produces NO event leaves a credit on the counter and swallows the operator's next real
   * gesture.
   *
   * <p>So the question is not "how many" but "whose". `dispatchAction` processes the action and
   * emits its events SYNCHRONOUSLY, inside the call -- so the overlay raises a flag, dispatches,
   * and lowers it in a `finally`. Everything announced inside that call is its own, however many
   * or few; everything announced outside it is a gesture. No arithmetic, nothing to accumulate,
   * and no dependence on a count or on a clock.
   *
   * <p>The one assumption left is that ECharts does not emit a straggler on a LATER frame. If it
   * ever did, the symptom would be an adopted window that is really the overlay's own, which
   * costs one unnecessary re-pan rather than a run with no tracking.
   */
  useEffect(() => {
    const instance = getChartInstance?.();
    if (!instance || typeof instance.on !== 'function') return undefined;
    const onZoom = (event) => {
      const auto = autoViewRef.current;
      if (auto.claiming) return;
      const window = readWindow(instance, event);
      if (!window) return;
      // ADOPTED AS THE REFERENCE, and as `last` too: without the second half the next tick would
      // compute a window identical to the one already on screen, find it different from the one
      // IT last sent, and dispatch -- undoing the gesture on the very next bar.
      auto.live = window;
      auto.last = window;
    };
    instance.on('datazoom', onZoom);
    return () => { if (typeof instance.off === 'function') instance.off('datazoom', onZoom); };
  }, [getChartInstance, timeline, session]);

  // The human book, read the same way and for the same reason: a draw scheduled by a bar tick
  // must paint the operator's latest fill rather than the one their closure happened to capture.
  const humanRef = useRef(dual.human);
  useEffect(() => { humanRef.current = dual.human; }, [dual.human]);



  useEffect(() => {
    if (!frame) return undefined;
    // THE WIPE OWNS THE CHART WHILE IT RUNS, AND THE PAUSE AFTER IT OWNS NOTHING. Without this
    // the live draw would re-reveal the bars the erase has just popped, on the same frame, and
    // the chart would flicker between the two — and the two-second empty beat would last one
    // frame, because this effect would put the pillars straight back up.
    if (!drawable) return undefined;
    pendingRef.current = frame;
    if (rafRef.current !== null) return undefined;   // a draw is already scheduled

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const target = pendingRef.current;
      const instance = getChartInstance?.();
      if (!target || !instance) return;

      const overlay = buildPlayheadOverlay(sessionDateOf(target.time), dates, {
        label: target.state,
      });
      if (!overlay) return;

      // Merged by series id, so ECharts touches these two series alone rather than rebuilding an
      // option carrying every candle.
      //
      // The annotations go in the SAME setOption as the playhead, deliberately. Two calls would
      // paint the amber line and the trade decorations on different frames, and at speed the
      // trendline would visibly trail the playhead it belongs to.
      // ONE setOption CARRYING ALL FIVE SERIES, and that is a performance decision as much as a
      // correctness one.
      //
      // Each is merged BY ID, so ECharts touches these five and leaves the 8,716 candles alone —
      // measured at ~74ms against a full option rebuild. Splitting them into five calls would
      // paint the playhead, the trade marks and the structure on different frames, so at speed
      // the decorations would visibly trail the amber line they belong to; it would also run
      // ECharts' merge-and-layout pass five times for one tick.
      const barDate = sessionDateOf(target.time);

      /*
       * THE PRICE, TRUNCATED AT THE PLAYHEAD — and in the SAME setOption as everything else.
       *
       * This is what makes the unveiling honest rather than decorative. Until now the replay drew
       * every bar and moved a line across them, so the annotations were bounded at the playhead
       * and the PRICE was not — a reader deciding at bar 400 could see bar 401, which is the one
       * thing they must not.
       *
       * It rides in the existing merge rather than a call of its own, for the reason the other
       * five do: one `setOption` is one merge-and-layout pass, so the candles, the playhead and
       * the structure all arrive on the same frame. Two calls would show the bar appearing a
       * frame before or after the line that marks it.
       *
       * A SHALLOW COPY is handed over rather than the buffer itself. Passing a reference ECharts
       * has already seen risks it short-circuiting on identity, and a copy of 9,000 references is
       * microseconds against a render it is about to do anyway.
       */
      // PRIMED draws the structure with the price EMPTY: the reveal buffer is floored at the
      // session's first bar and has revealed nothing, so handing it over as it stands IS the
      // empty canvas. `revealTo` is simply not called until the phase goes live.
      const reveal = revealRef.current;
      // NO BARS MEANS NO PATCH AT ALL, and that guard is load-bearing rather than defensive.
      // Without it a caller who omits `barData` gets a buffer of pure nulls merged onto the price
      // series — which BLANKS THE CHART. Losing the unveiling is an acceptable cost of a
      // forgotten prop; losing the price is not.
      const priceSeries = reveal && barDataRef.current
        ? (() => {
          if (live) revealTo(reveal, barDataRef.current, indexRef.current);
          return pricePatchSeries(
            chartTypeRef.current, [...reveal.data], [...reveal.volumes]);
        })()
        : [];

      instance.setOption({
        series: [
          ...priceSeries,
          { id: REPLAY_PLAYHEAD_SERIES_ID, ...playheadMarks(overlay) },
          {
            id: REPLAY_ANNOTATION_SERIES_ID,
            ...annotationMarks(timelineRef.current, dates, indexRef.current,
              target.diagnostics ? target.diagnostics.trendline : null),
          },
          {
            // THE PTA/PTB RAYS, which GROW as the playhead passes each band's close. The bands
            // themselves are a fixed calendar and never change; a line's LEVEL is an extreme of
            // its band's bars, so it may not be drawn until that band has closed -- see
            // timeBandOverlay. Merged in the same setOption as the other two so the structure
            // can never appear a frame after the bar that revealed it.
            //
            // AGAINST THE FULL AXIS, so a band and its rays reach into the synthetic future the
            // chart already draws. See the note on `axis` above: this used to be handed the
            // unpadded list, so the forward structure was drawn once at mount and erased by the
            // first tick.
            // FILTERED BY THE SESSION'S OWN TOGGLES, which is the visualisation half of
            // "execution and visualisation are independent": the canvas draws every category the
            // operator ticked, whatever the algo book is trading.
            id: TIME_BAND_SERIES_ID,
            ...(() => {
              const bands = visibleBands(timelineRef.current?.timeBands, session?.overlays);
              const marks = timeBandMarks(bands, axis, barDate);
              /*
               * THE BAND FORMING, appended to the engine's own rays.
               *
               * While the playhead is INSIDE a generation interval the band's extremes do not
               * exist yet — not in the engine's table and not anywhere — because they are the
               * extremes of bars nobody has seen. So they are drawn as they accumulate, from
               * REVEALED bars only, dashed and half-opacity and labelled `forming` so they cannot
               * be mistaken for a settled level.
               *
               * Appended to the SAME markLine rather than given a series of its own: they are the
               * same category's levels at a different stage of knowing, and a second series would
               * need its own z-order and could eventually draw in the wrong order relative to the
               * rays it belongs with.
               *
               * This does NOT weaken the rule that the engine's levels are never recomputed here.
               * Those still arrive from the backend and are still drawn only from `validFrom`.
               * See formingBands.js.
               */
              const forming = formingRays(
                bands, axis, barDataRef.current, indexRef.current,
                session ? bounds.from : 0);
              return forming.length === 0
                ? marks
                : {
                  ...marks,
                  markLine: { ...marks.markLine, data: [...marks.markLine.data, ...forming] },
                };
            })(),
          },
          {
            // CTS TIMING PILLARS — all of them, forward ones included, for the same reason the
            // bands are: a timing point is pure calendar. Empty when the session did not ask for
            // them, rather than absent: a merge cannot remove a mark by omitting it.
            id: CTS_PILLAR_SERIES_ID,
            ...ctsPillarMarks(
              !session || session.overlays.CTS ? ctsPoints : [], axis, barDate).rail,
          },
          {
            /*
             * ...and the marks whose bars the playhead HAS reached, at their price.
             *
             * <p>A markPoint's coord is read against its own series' y axis, so the two halves
             * cannot share a series: the rail one is a 0..1 scale and a price of 450 handed to
             * it lands off the top of the world. This is what makes a mark SNAP onto the chart
             * as the clock passes it — it leaves one series' payload and joins the other's, on
             * the same merge, in the same frame.
             */
            id: CTS_PILLAR_PRICE_SERIES_ID,
            ...ctsPillarMarks(
              !session || session.overlays.CTS ? ctsPoints : [], axis, barDate).price,
          },
          {
            /*
             * THE ACCELERATING FAN, drawn as it steepened.
             *
             * <p>Bounded twice: a line appears only once the playhead reaches the PIVOT that
             * established it -- a pivot is a price extreme of a bar, so drawing one early is
             * look-ahead -- and each segment is then clipped at the playhead so its tail does
             * not reach into unrevealed bars.
             *
             * <p>Against `dates`, the REAL bars, not the padded axis: this carries prices, and
             * the padded columns are synthetic. The band structure beside it uses `axis` for the
             * opposite reason -- it is calendar and has no price in it at all.
             */
            id: FAN_SERIES_ID,
            ...fanLineMarks(timelineRef.current?.trades, barDate),
          },
          {
            // BOTH BOOKS' FILLS. Solid arrows for the engine, hollow triangles for the operator,
            // each bounded at the playhead — the human side by DATE, which is not redundant: the
            // transport steps backwards, and an operator must not be shown their own future fill.
            id: LEDGER_MARKER_SERIES_ID,
            ...ledgerMarkerMarks(timelineRef.current, humanRef.current, dates,
              indexRef.current, barDate),
          },
        ],
      });

      /*
       * WHERE THE CHART LOOKS, and the session answers it in two different ways.
       *
       *   PRIMED   the session's WHOLE range — the structure standing across what is coming
       *   LIVE     about a year, sliding, so a candle being unveiled is a candle and not a pixel
       *
       * `viewWindowFor` puts the playhead 72% across, which is right for a research replay where
       * the past is drawn and wrong for a session, whose past was just erased: 72% of the view
       * would be empty canvas on the LEFT. It stays the answer for a replay with no session.
       */
      const auto = autoViewRef.current;
      const view = (() => {
        if (!session) return viewWindowFor(overlay.barIndex, (dates || []).length);
        if (!live) return primedViewWindow(bounds.from, bounds.to, (axis || []).length);
        /*
         * THRESHOLD PANNING, against the window the ANIMATION is using.
         *
         * `auto.live` rather than `auto.last`: the last window dispatched is the PRIMED one, the
         * session's whole range, and the playhead is inside it — so a threshold pan measured
         * against that would decide nothing needs to move and the animation would run for its
         * whole length at the primed scale, never zooming in at all.
         */
        return sessionViewWindow(
          overlay.barIndex, bounds.from, auto.live, (axis || []).length);
      })();

      /*
       * AND THE OPERATOR CAN TAKE IT OFF US.
       *
       * Panning the view on every tick is what makes the animation watchable and is also a fight
       * nobody can win: a reader who zooms out to look at the structure has the chart snatched
       * back on the next bar, which reads as a broken chart rather than as a feature. So the
       * first datazoom this overlay did NOT dispatch surrenders the view for the rest of the run.
       *
       * Detected by counting our own dispatches rather than by reading the chart's option back:
       * `getOption()` clones the whole option — 8,716 candles — and doing that once a bar to find
       * out where the window is would cost more than the draw it is guarding.
       *
       * A window identical to the last one we sent is NOT dispatched, which is what keeps the
       * count honest: a no-op dispatch that ECharts decides not to announce would leave a credit
       * on the counter and swallow the operator's next gesture.
       */
      const unchanged = view && auto.last
        && auto.last.startValue === view.startValue && auto.last.endValue === view.endValue;
      if (view && !unchanged) {
        auto.last = view;
        if (live && session) auto.live = view;
        // RAISED BEFORE the dispatch, because ECharts answers it synchronously: the handler runs
        // inside the call below, so a flag raised afterwards would arrive too late to claim it.
        // startValue/endValue, NOT start/end: EChartComponent's datazoom handler records only
        // those two into zoomRef, so a percentage dispatch moves the view and is never recorded
        // — and the next option rebuild snaps it back. A comment in that handler documents the
        // same failure breaking Elliott's "focus on the count".
        //
        // dispatchAction, never setOption: this moves the dataZoom component and nothing else,
        // where a setOption carrying a dataZoom would re-run the merge-and-layout pass over
        // every series on the chart for a change that touches one of them.
        auto.claiming = true;
        try {
          instance.dispatchAction({
            type: 'dataZoom', startValue: view.startValue, endValue: view.endValue,
          });
        } finally {
          // `finally`, so a throw inside ECharts cannot leave the flag raised -- which would
          // make every later gesture read as the overlay's and take the view away for good.
          auto.claiming = false;
        }
      }
    });
    return undefined;
    // `dual.human` is a dependency so a human fill appears on the bar it was taken on rather than
    // on the next tick of the clock — a marker that arrived a bar late would be drawn against the
    // wrong candle, which in an audit is worse than not drawing it.
  }, [frame, dates, axis, ctsPoints, dual.human, session, barData, chartType, drawable, live,
    bounds.from, bounds.to, getChartInstance]);

  // A replay closed mid-flight must not draw onto a chart that has moved on.
  useEffect(() => () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
  }, []);

  if (!timeline || !frame) return null;

  return (
    // pointerEvents none on the layer, auto on the transport: the chart underneath stays fully
    // interactive (hover a candle, read its tooltip) while the replay is running.
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none' }}>
      <ReplayPositionHud state={state} />

      {/*
        ONE DOCKED COLUMN, not two panels on opposite edges.

        The ledger was on the left and the engine state on the right, which on a wide monitor put
        the two halves of one question a screen apart — "the book is down 2%" and "which gate is
        about to fire" are read together, and the eyes were doing the joining.

        Stacked, so the order is the order they are consulted: the book first (where am I), then
        the FSM's reasoning for this bar (why). Scrolls as one unit; `pointerEvents: auto` only on
        the column, so the chart underneath stays hoverable everywhere else.
      */}
      <Box
        data-testid="replay-debug-dock"
        sx={{
          // 70 from the right, not 20: the dock sat over the newest candles, which is exactly the
          // price action a reader is checking the engine's decision against. 50px of clearance is
          // about two candle widths at the replay's default zoom — enough to read the last bars
          // without giving up dock width, which is what moving it left instead would have cost.
          position: 'absolute', top: 20, right: DOCK_RIGHT_PX, zIndex: 11,
          width: 404, maxHeight: 'calc(100% - 120px)', overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: 1.25,
          pointerEvents: 'auto',
        }}
      >
        {/* NOT memoised any more, and that is a deliberate trade.

            `MemoReplayLedgerHud` compares only `state.stats` and `state.account`, which is right
            for a single-book HUD whose numbers change a handful of times in 2,000 bars. The
            comparative view also has to re-render when the HUMAN book changes, and a human action
            moves neither of those two — so under the memo an operator's fill would appear in the
            ledger on the next bar that happened to close an algo trade.

            The cost is a re-render of three panels per tick, which is what the component-scope
            `sx` objects and module-level subcomponents at the top of this file exist to make
            cheap. The memo is kept exported, because a caller with no copilot still wants it. */}
        <ReplayLedgerHud
          // THE RUN'S OWN SYMBOL, not the chart's. They are the same today because changing the
          // chart's symbol exits the replay -- but the figures in this panel were computed from
          // the timeline, so the caption has to come from the same place they did.
          symbol={timeline.symbol}
          state={state}
          sized={sized}
          comparison={displayComparison}
          dual={dual}
          sizingModel={sizingModel}
          onSizingModelChange={frozen ? null : setSizingModel}
          onHumanAction={frozen ? null : handleHumanAction}
          currentBar={barAt(timeline, clock.index)}
          frozen={frozen}
          sessionScoped={!!session}
          autoProgression={autoProgression}
          // BOUNDED AT THE PLAYHEAD, like every figure in the panel above it. The whole run's
          // trades would put years the reader has not reached into a table they are being asked
          // to form a view from.
          closedTrades={tradesClosedBy(timeline, clock.index)}
          // THE SCOPE, so the heading can say what the figures cover. Reading this panel at two
          // different bars gave two different numbers with nothing to explain it, which was
          // reported as the replay being non-deterministic.
          barIndex={clock.index}
          barTotal={clock.total}
          clickPen={clickPen}
          onFocusRange={handleFocusRange}
          // HIDDEN WHILE FROZEN, like every other control that would change the snapshot. The
          // gesture itself is refused by the resolver's `enabled` flag, so this is the visible
          // half of one rule rather than a second one.
          onClickPenChange={frozen ? null : setClickPen}
        />
        <DynaManDebugPanel
          frame={frame}
          stopUpdates={stopUpdatesAt(timeline, clock.index)}
          atBreakpoint={clock.atBreakpoint}
        />
      </Box>

      <ReplayTransport
        onPage={handlePage}
        clock={clock}
        state={state}
        symbol={timeline.symbol}
        onClose={onClose}
        session={session}
        autoProgression={autoProgression}
        intro={live ? null : introPhase}
        mandate={session ? session.mandate : null}
        onMandateChange={onMandateChange}
        mandateLoading={mandateLoading}
        frozen={frozen}
        onTerminate={handleTerminate}
        onResetSession={handleResetSession}
        nextTrade={nextTrade}
        previousTrade={previousTrade}
        onNextTrade={() => nextTrade !== null && clock.jump(nextTrade)}
        onPreviousTrade={() => previousTrade !== null && clock.jump(previousTrade)}
        tradeCount={breakpoints.size}
        tradeOrdinal={tradeOrdinal}
        tradeExit={tradeExit}
        onAutoPlayTrade={() => {
          // Toggles: a second press pauses rather than restarting, which is what a reader reaches
          // for when something on the chart deserves a longer look.
          if (clock.playing) {
            clock.pause();
          } else if (tradeExit !== null) {
            clock.playTo(tradeExit, AUTO_PLAY_DELAY_MS);
          }
        }}
      />
    </Box>
  );
}

DynaManReplayOverlay.propTypes = {
  timeline: PropTypes.object,
  /**
   * The REAL bars' dates. Everything price-bearing is bounded by these, so a decoration can never
   * be drawn on a synthetic future column where there is no price.
   */
  dates: PropTypes.array,
  /**
   * The chart's FULL axis, padding included. Only the calendar structure uses it — the bands,
   * their rays and the CTS pillars — which is what lets them stand in the empty space ahead of
   * the bars. Falls back to `dates`, so a caller that omits it loses the forward half and nothing
   * else.
   */
  axisDates: PropTypes.array,
  /** Returns the live ECharts instance. A getter, not the instance: it exists after mount. */
  getChartInstance: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  initialDelayMs: PropTypes.number,
  /**
   * THE ACCOUNT THE ALGO RUN WAS PRICED WITH — the same object the replay request carried.
   *
   * <p>This is cost parity, and it is the condition on the whole comparison: the human book is
   * charged the identical commission and slippage through `accountCosts`, which mirrors
   * `AccountSimulation`. Absent means frictionless, which does not error and does not look
   * wrong — it just hands the operator free execution and lets them win by the friction they were
   * not charged. `DynaManPhase2.test.js` asserts App passes it for exactly that reason.
   */
  account: PropTypes.object,
  /**
   * CTS timing points, from the scan already drawn on the chart: `{date, price, firedOn}`.
   *
   * <p>Past AND future. Taken from the existing overlay rather than loaded again here, so the
   * pillars cannot disagree with the violet triangles beside them about which days are timing
   * points — one dataset read, one fold, one bar alignment.
   */
  ctsPoints: PropTypes.array,
  /**
   * THE SESSION: `{startDate, endDate, overlays, mandate}`, or null.
   *
   * <p>Null is the research dialog's path and keeps every earlier behaviour: the whole run is
   * walked, every band the run produced is drawn, and no mandate selector appears — that dialog
   * carries its own provider and its own levers, and offering to swap the provider there would
   * discard a configuration built one control at a time.
   */
  session: PropTypes.object,
  /**
   * Called with a mandate value. The caller re-fetches, because the algo book is the backend's.
   *
   * <p>Absent hides the selector rather than offering one that cannot change anything.
   */
  onMandateChange: PropTypes.func,
  mandateLoading: PropTypes.bool,
  /**
   * THE CHART'S OWN `{dates, ohlc, volumes}` — the REAL bars, not the padded series.
   *
   * <p>What the reveal buffer reads. Absent means no truncation happens at all and the chart keeps
   * drawing every bar, which is the pre-Phase-3 behaviour: a caller that forgets this loses the
   * unveiling and nothing else.
   */
  barData: PropTypes.object,
  /**
   * 'candlestick' | 'line' | 'hidden'. The price patch must RESTATE the type, and restating the
   * wrong one silently converts a line chart into candles mid-run — a recorded defect, which is
   * why this is a prop rather than an assumption.
   */
  chartType: PropTypes.string,
  /**
   * How long the erase sequence takes, in milliseconds. Defaults to {@link INTRO_ERASE_MS}.
   *
   * <p>0 skips the theatre entirely and starts LIVE — which is what a test that is not about the
   * intro passes, because an animation in front of an assertion is a wait rather than a subject.
   * The sequence only ever runs for a SESSION; the research dialog's replay is unchanged.
   */
  introMs: PropTypes.number,
};
